import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axiosInstance";
import useCrossTabDataRefreshToken from "../hooks/useCrossTabDataRefresh";
import { usePmsTheme } from "./PmsThemeContext";
import { useNavigate } from "react-router-dom";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import { demoReferenceDate } from "../demo/demoSnapshot";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";

const INITIAL_MATRIX_RANGE_DAYS = 45;
const MAX_MATRIX_RANGE_DAYS = 180;
const DEFAULT_VISIBLE_DAYS = 12;
const STOP_SELL_BATCH_WINDOW_MS = 650;
const DEMO_MATRIX_MESSAGES = {
  en: { readOnly: "Read-only demo", disabled: "Read-only demo: prices, restrictions and stop sells cannot be changed.", retry: "Retry", refresh: "Refresh", update: "Update the demo exporter and JSON to display Room Rates Matrix." },
  es: { readOnly: "Solo consulta", disabled: "Demo de solo consulta: no se pueden cambiar precios, restricciones ni stop sells.", retry: "Reintentar", refresh: "Actualizar", update: "Actualiza el exportador y el JSON de la demo para mostrar Room Rates Matrix." },
  ca: { readOnly: "Només consulta", disabled: "Demo de només consulta: no es poden canviar preus, restriccions ni stop sells.", retry: "Reintentar", refresh: "Actualitzar", update: "Actualitza l'exportador i el JSON de la demo per mostrar Room Rates Matrix." },
};

const ROOM_RATES_MATRIX_MESSAGES = {
  en: {
    title: "Room Rates Matrix",
    subtitle: {
      loading: "Loading room rates matrix...",
      ready: (filteredRows, totalRows, loadedRange, maxDays) =>
        `${filteredRows}/${totalRows} rows | loaded range: ${loadedRange} | max selectable range: ${maxDays} days`,
    },
    toolbar: {
      from: "From",
      to: "To",
      resetRange: "Reset range",
      resetRangeTitle: "Reset visible range to first loaded days",
      bulkApply: "Bulk Apply",
      stopSellsOverview: "Stop Sells Overview",
    },
    range: {
      selected: (start, end, count) => `${start} -> ${end} (${count} days)`,
      noneSelected: "No dates selected",
      loaded: (start, end, count) => `${start} -> ${end} (${count} days loaded)`,
      noneLoaded: "No dates loaded",
    },
    queue: {
      synced: "All changes synced",
      queuedChanges: (count) => `${count} queued change${count === 1 ? "" : "s"}`,
      stopSellError: (message) => `Could not update stop sell: ${message}`,
      priceSaveError: (message) => `At least one queued change failed: ${message}`,
    },
    stopSellNotice: {
      title: "Could not apply the change",
      intro: "Pilcore restored the real status. Please review these dates:",
      closeSale: "Close sales",
      openSale: "Open sales",
      confirm: "Understood",
      unknownRate: "Rate",
    },
    states: {
      loadingTitle: "Loading matrix...",
      loadingSubtitle: "Please wait while prices and constraints are loaded.",
      loadErrorTitle: "Could not load room rates matrix.",
      noDataTitle: "No matrix data.",
      noDataSubtitle: "Adjust the selected date range or verify source data.",
      noFilteredTitle: "No rows match active filters.",
      noFilteredSubtitle: "Clear one or more column filters to see rows again.",
    },
    headers: {
      roomCategory: "Room category",
      roomRate: "Room rate",
      client: "Client",
      dependentFrom: "Dependent from",
    },
    filters: {
      roomCategory: "Room category",
      roomRate: "Room rate",
      client: "Client",
      dependentFrom: "Dependent from",
      all: "All",
      clear: "Clear",
      close: "Close",
      categoriesCount: (count) => `${count} categories`,
      ratesCount: (count) => `${count} rates`,
      clientsCount: (count) => `${count} clients`,
      selectedCount: (count) => `${count} selected`,
      searchIn: (title) => `Search in ${String(title || "").toLowerCase()}...`,
      noMatchingOptions: "No matching options.",
      selectHint: "Select one or multiple values.",
    },
    actions: {
      openRoomRate: "Open room rate",
    },
    cell: {
      readOnlyRateAlert: "This rate is dependent or formula-linked. Price cannot be modified manually, but min/max stay can.",
      invalidNumber: "Invalid number.",
      invalidCurrencyPrecision: (currency, minorUnits) =>
        `${currency} accepts ${minorUnits === 0 ? "whole-unit prices only" : `up to ${minorUnits} decimal places`}.`,
      missingRateId: "Cannot save: missing room_rate_id.",
      updatingStopSell: "Updating stop sell...",
      removeStopSell: "Open sales",
      addStopSell: "Close sales",
      stopSellActive: "Sales are closed. Price editing is still available.",
      automaticStopSell: "Automatic stop sell. Update Client Category Allocation to reopen sales.",
      clickToEditPrice: "Click to edit price",
      minShort: "min",
      maxShort: "max",
      modalPrice: "Price",
      modalMinStay: "Min stay",
      modalMaxStay: "Max stay",
      modalCellCount: (n) => `${n} cells`,
    },
    messages: {
      loadError: "Error loading matrix data.",
      noBusinessUnit: "No active business unit selected.",
      unknownError: "Unknown error",
    },
  },
  es: {
    title: "Matriz de tarifas de habitaciones",
    subtitle: {
      loading: "Cargando matriz de tarifas...",
      ready: (filteredRows, totalRows, loadedRange, maxDays) =>
        `${filteredRows}/${totalRows} filas | rango cargado: ${loadedRange} | rango maximo seleccionable: ${maxDays} dias`,
    },
    toolbar: {
      from: "Desde",
      to: "Hasta",
      resetRange: "Restablecer rango",
      resetRangeTitle: "Restablecer el rango visible a los primeros dias cargados",
      bulkApply: "Aplicacion masiva",
      stopSellsOverview: "Resumen de stop sells",
    },
    range: {
      selected: (start, end, count) => `${start} -> ${end} (${count} dias)`,
      noneSelected: "Sin fechas seleccionadas",
      loaded: (start, end, count) => `${start} -> ${end} (${count} dias cargados)`,
      noneLoaded: "Sin fechas cargadas",
    },
    queue: {
      synced: "Todos los cambios sincronizados",
      queuedChanges: (count) => `${count} cambio${count === 1 ? "" : "s"} en cola`,
      stopSellError: (message) => `No se pudo actualizar el stop sell: ${message}`,
      priceSaveError: (message) => `Al menos un cambio en cola ha fallado: ${message}`,
    },
    stopSellNotice: {
      title: "No se pudo aplicar el cambio",
      intro: "Pilcore ha recuperado el estado real. Revisa estas fechas:",
      closeSale: "Cerrar ventas",
      openSale: "Abrir ventas",
      confirm: "Entendido",
      unknownRate: "Tarifa",
    },
    states: {
      loadingTitle: "Cargando matriz...",
      loadingSubtitle: "Espera mientras se cargan precios y restricciones.",
      loadErrorTitle: "No se pudo cargar la matriz de tarifas.",
      noDataTitle: "No hay datos en la matriz.",
      noDataSubtitle: "Ajusta el rango de fechas seleccionado o verifica los datos de origen.",
      noFilteredTitle: "No hay filas que coincidan con los filtros activos.",
      noFilteredSubtitle: "Limpia uno o varios filtros de columna para volver a ver filas.",
    },
    headers: {
      roomCategory: "Categoria de habitacion",
      roomRate: "Tarifa",
      client: "Cliente",
      dependentFrom: "Dependiente de",
    },
    filters: {
      roomCategory: "Categoria de habitacion",
      roomRate: "Tarifa",
      client: "Cliente",
      dependentFrom: "Dependiente de",
      all: "Todas",
      clear: "Limpiar",
      close: "Cerrar",
      categoriesCount: (count) => `${count} categorias`,
      ratesCount: (count) => `${count} tarifas`,
      clientsCount: (count) => `${count} clientes`,
      selectedCount: (count) => `${count} seleccionados`,
      searchIn: (title) => `Buscar en ${String(title || "").toLowerCase()}...`,
      noMatchingOptions: "No hay opciones que coincidan.",
      selectHint: "Selecciona uno o varios valores.",
    },
    actions: {
      openRoomRate: "Abrir tarifa",
    },
    cell: {
      readOnlyRateAlert: "Esta tarifa es dependiente o esta enlazada por formula. El precio no se puede modificar manualmente, pero si el min/max estancia.",
      invalidNumber: "Numero no valido.",
      invalidCurrencyPrecision: (currency, minorUnits) =>
        `${currency} admite ${minorUnits === 0 ? "solo precios en unidades enteras" : `hasta ${minorUnits} decimales`}.`,
      missingRateId: "No se puede guardar: falta room_rate_id.",
      updatingStopSell: "Actualizando stop sell...",
      removeStopSell: "Abrir ventas",
      addStopSell: "Cerrar ventas",
      stopSellActive: "Ventas cerradas. La edicion del precio sigue disponible.",
      automaticStopSell: "Stop sell automatico. Modifica Client Category Allocation para volver a abrir ventas.",
      clickToEditPrice: "Haz clic para editar el precio",
      minShort: "min",
      maxShort: "max",
      modalPrice: "Precio",
      modalMinStay: "Min estancia",
      modalMaxStay: "Max estancia",
      modalCellCount: (n) => `${n} celdas`,
    },
    messages: {
      loadError: "Error al cargar los datos de la matriz.",
      noBusinessUnit: "No hay una unidad de negocio activa seleccionada.",
      unknownError: "Error desconocido",
    },
  },
  ca: {
    title: "Matriu de tarifes d'habitacions",
    subtitle: {
      loading: "Carregant la matriu de tarifes...",
      ready: (filteredRows, totalRows, loadedRange, maxDays) =>
        `${filteredRows}/${totalRows} files | rang carregat: ${loadedRange} | rang maxim seleccionable: ${maxDays} dies`,
    },
    toolbar: {
      from: "Des de",
      to: "Fins a",
      resetRange: "Restablir rang",
      resetRangeTitle: "Restablir el rang visible als primers dies carregats",
      bulkApply: "Aplicacio massiva",
      stopSellsOverview: "Resum de stop sells",
    },
    range: {
      selected: (start, end, count) => `${start} -> ${end} (${count} dies)`,
      noneSelected: "Sense dates seleccionades",
      loaded: (start, end, count) => `${start} -> ${end} (${count} dies carregats)`,
      noneLoaded: "Sense dates carregades",
    },
    queue: {
      synced: "Tots els canvis sincronitzats",
      queuedChanges: (count) => `${count} canvi${count === 1 ? "" : "s"} en cua`,
      stopSellError: (message) => `No s'ha pogut actualitzar l'stop sell: ${message}`,
      priceSaveError: (message) => `Com a minim un canvi en cua ha fallat: ${message}`,
    },
    stopSellNotice: {
      title: "No s'ha pogut aplicar el canvi",
      intro: "Pilcore ha recuperat l'estat real. Revisa aquestes dates:",
      closeSale: "Tancar vendes",
      openSale: "Obrir vendes",
      confirm: "Entes",
      unknownRate: "Tarifa",
    },
    states: {
      loadingTitle: "Carregant matriu...",
      loadingSubtitle: "Espera mentre es carreguen preus i restriccions.",
      loadErrorTitle: "No s'ha pogut carregar la matriu de tarifes.",
      noDataTitle: "No hi ha dades a la matriu.",
      noDataSubtitle: "Ajusta el rang de dates seleccionat o verifica les dades d'origen.",
      noFilteredTitle: "No hi ha files que coincideixin amb els filtres actius.",
      noFilteredSubtitle: "Neteja un o mes filtres de columna per tornar a veure files.",
    },
    headers: {
      roomCategory: "Categoria d'habitacio",
      roomRate: "Tarifa",
      client: "Client",
      dependentFrom: "Dependent de",
    },
    filters: {
      roomCategory: "Categoria d'habitacio",
      roomRate: "Tarifa",
      client: "Client",
      dependentFrom: "Dependent de",
      all: "Totes",
      clear: "Netejar",
      close: "Tancar",
      categoriesCount: (count) => `${count} categories`,
      ratesCount: (count) => `${count} tarifes`,
      clientsCount: (count) => `${count} clients`,
      selectedCount: (count) => `${count} seleccionats`,
      searchIn: (title) => `Buscar a ${String(title || "").toLowerCase()}...`,
      noMatchingOptions: "No hi ha opcions coincidents.",
      selectHint: "Selecciona un o diversos valors.",
    },
    actions: {
      openRoomRate: "Obrir tarifa",
    },
    cell: {
      readOnlyRateAlert: "Aquesta tarifa es dependent o esta enllacada per formula. El preu no es pot modificar manualment, pero si el min/max estada.",
      invalidNumber: "Numero no valid.",
      invalidCurrencyPrecision: (currency, minorUnits) =>
        `${currency} admet ${minorUnits === 0 ? "nomes preus en unitats senceres" : `fins a ${minorUnits} decimals`}.`,
      missingRateId: "No es pot desar: falta room_rate_id.",
      updatingStopSell: "Actualitzant stop sell...",
      removeStopSell: "Obrir vendes",
      addStopSell: "Tancar vendes",
      stopSellActive: "Vendes tancades. L'edicio del preu continua disponible.",
      automaticStopSell: "Stop sell automatic. Modifica Client Category Allocation per tornar a obrir vendes.",
      clickToEditPrice: "Fes clic per editar el preu",
      minShort: "min",
      maxShort: "max",
      modalPrice: "Preu",
      modalMinStay: "Min estada",
      modalMaxStay: "Max estada",
      modalCellCount: (n) => `${n} cel·les`,
    },
    messages: {
      loadError: "Error en carregar les dades de la matriu.",
      noBusinessUnit: "No hi ha cap unitat de negoci activa seleccionada.",
      unknownError: "Error desconegut",
    },
  },
};

function normalizeIsoDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function isAutomaticStopSell(stopSell) {
  return stopSell?.is_automatic === true || stopSell?.is_automatic === "true" || stopSell?.is_automatic === 1;
}

function addDaysIso(isoDate, days) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return isoDate;
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().split("T")[0];
}

function dayDiff(startIso, endIso) {
  const start = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.floor((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

function clampIsoDate(isoDate, minIso, maxIso) {
  if (!isoDate) return "";
  if (isoDate < minIso) return minIso;
  if (isoDate > maxIso) return maxIso;
  return isoDate;
}

function sanitizeRange(startIso, endIso, allDates) {
  if (!Array.isArray(allDates) || allDates.length === 0) {
    return { start: "", end: "" };
  }

  const minIso = allDates[0];
  const maxIso = allDates[allDates.length - 1];

  let start = clampIsoDate(normalizeIsoDate(startIso) || minIso, minIso, maxIso);
  let end = clampIsoDate(normalizeIsoDate(endIso) || start, minIso, maxIso);

  if (end < start) end = start;

  const selectedDays = dayDiff(start, end) + 1;
  if (selectedDays > MAX_MATRIX_RANGE_DAYS) {
    end = addDaysIso(start, MAX_MATRIX_RANGE_DAYS - 1);
    end = clampIsoDate(end, minIso, maxIso);
  }

  if (end < start) start = end;

  return { start, end };
}

function sanitizeRequestRange(startIso, endIso, todayIso) {
  let start = normalizeIsoDate(startIso) || todayIso;
  if (start < todayIso) start = todayIso;

  const defaultEnd = addDaysIso(start, DEFAULT_VISIBLE_DAYS - 1);
  const endMax = addDaysIso(start, MAX_MATRIX_RANGE_DAYS - 1);
  let end = clampIsoDate(normalizeIsoDate(endIso) || defaultEnd, start, endMax);

  if (end < start) end = start;

  return { start, end };
}

function rangeIsLoaded(range, loadedDates) {
  if (!range?.start || !range?.end || !Array.isArray(loadedDates) || loadedDates.length === 0) {
    return false;
  }

  return loadedDates[0] <= range.start && loadedDates[loadedDates.length - 1] >= range.end;
}

function sortRows(rows) {
  return [...rows].sort((a, b) => {
    const categoryCmp = String(a.room_category || "").localeCompare(String(b.room_category || ""));
    if (categoryCmp !== 0) return categoryCmp;

    const rateCmp = String(a.room_rate || "").localeCompare(String(b.room_rate || ""));
    if (rateCmp !== 0) return rateCmp;

    return String(a.client || "").localeCompare(String(b.client || ""));
  });
}

function rowCurrencyCatalog(row) {
  return row?.currency
    ? [{ code: row.currency, minor_units: row.minor_units }]
    : [];
}

export function matrixPriceHasValidPrecision(price, cells = []) {
  if (price === "" || price === null || price === undefined) return true;
  return cells.every((cell) =>
    hasValidCurrencyPrecision(price, cell?.currency, rowCurrencyCatalog(cell))
  );
}

function formatPrice(value, locale, row) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  const hasCurrencyMetadata = /^[A-Z]{3}$/.test(String(row?.currency || "").toUpperCase());
  const minorUnits = hasCurrencyMetadata
    ? getCurrencyMinorUnits(row.currency, rowCurrencyCatalog(row))
    : (Number.isInteger(numeric) ? 0 : 2);
  return numeric.toLocaleString(locale, {
    minimumFractionDigits: minorUnits,
    maximumFractionDigits: minorUnits,
  });
}

function toDayLabel(isoDate, locale) {
  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
}

function rowKeyFor(row) {
  return `${row?.room_rate_id || "no-rate"}|${row?.client_id || row?.client || "no-client"}`;
}

function getDefaultMatrixColumnFilters() {
  return {
    roomCategory: [],
    roomRate: [],
    client: [],
    dependentFrom: [],
  };
}

function normalizeFilterLabel(value) {
  const text = String(value ?? "").trim();
  return text || "-";
}

function normalizeFilterValue(value) {
  return normalizeFilterLabel(value).toLowerCase();
}

const uiCss = `
  .rrm-page {
    flex: 1 1 auto;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 4px 10px 4px;
    height: calc(100dvh - 28px);
    min-height: calc(100dvh - 28px);
    max-height: calc(100dvh - 28px);
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    box-sizing: border-box;
    overflow: hidden;
  }

  @media (max-width: 768px) {
    .rrm-page {
      padding: 4px 8px 4px;
    }
  }

  @media (max-height: 820px) {
    .rrm-page {
      padding-block: 3px;
    }
  }

  @media (max-height: 720px) {
    .rrm-page {
      padding-block: 2px;
    }
  }

  @media (max-height: 620px) {
    .rrm-page {
      padding-block: 1px;
    }
  }

  .rrm-card {
    position: relative;
    flex: 1 1 auto;
    height: 100%;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
    max-height: none;
    border-radius: 18px;
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(25, 25, 51, 0.94), rgba(10, 10, 22, 0.94)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.55), rgba(168, 85, 247, 0.65), rgba(255, 77, 79, 0.45)) border-box;
    box-shadow: 0 28px 90px rgba(0, 0, 0, 0.58);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .rrm-card::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(900px 300px at 10% 0%, rgba(48, 216, 255, 0.14), transparent 60%),
      radial-gradient(700px 260px at 90% 10%, rgba(168, 85, 247, 0.16), transparent 62%),
      radial-gradient(800px 260px at 40% 110%, rgba(255, 77, 79, 0.10), transparent 60%);
    pointer-events: none;
    opacity: 0.95;
  }

  .rrm-card > * {
    position: relative;
    z-index: 1;
  }

  .rrm-cardHeader {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    padding: 18px 18px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    flex: 0 0 auto;
  }

  @media (max-width: 1160px) {
    .rrm-cardHeader {
      flex-direction: column;
      align-items: stretch;
    }
  }

  .rrm-title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 800;
    letter-spacing: 0.2px;
    color: transparent;
    background: linear-gradient(90deg, #e6e6f7 0%, rgba(48, 216, 255, 0.95) 42%, rgba(168, 85, 247, 0.95) 85%);
    -webkit-background-clip: text;
    background-clip: text;
    text-shadow: 0 0 24px rgba(48, 216, 255, 0.12);
  }

  .rrm-subtitle {
    margin-top: 4px;
    font-size: 0.85rem;
    color: rgba(230, 230, 247, 0.72);
  }

  .rrm-toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .rrm-dateControls,
  .rrm-toolbarActions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }

  .rrm-toolbarActions {
    margin-left: 10px;
  }

  .rrm-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: rgba(230, 230, 247, 0.74);
    font-size: 0.78rem;
  }

  .rrm-input {
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(230, 230, 247, 0.95);
    padding: 6px 9px;
    font-size: 0.8rem;
  }

  .rrm-input:focus {
    outline: none;
    border-color: rgba(48, 216, 255, 0.75);
    box-shadow: 0 0 0 2px rgba(48, 216, 255, 0.16);
  }

  .rrm-btn {
    border-radius: 11px;
    padding: 7px 11px;
    font-size: 0.8rem;
    font-weight: 700;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.04);
    color: rgba(230, 230, 247, 0.9);
    cursor: pointer;
    transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
  }

  .rrm-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 24px rgba(0, 0, 0, 0.25);
  }

  .rrm-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  .rrm-btn--green {
    border-color: rgba(64, 255, 170, 0.28);
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.22), rgba(20, 184, 166, 0.15));
    color: rgba(212, 255, 232, 0.95);
  }

  .rrm-btn--amber {
    border-color: rgba(255, 198, 95, 0.34);
    background: linear-gradient(135deg, rgba(245, 158, 11, 0.22), rgba(251, 191, 36, 0.13));
    color: rgba(255, 238, 203, 0.96);
  }

  .rrm-queueBadge {
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 0.72rem;
    font-weight: 700;
    border: 1px solid rgba(255, 255, 255, 0.18);
    color: rgba(230, 230, 247, 0.9);
    background: rgba(255, 255, 255, 0.05);
  }

  .rrm-queueBadge.isActive {
    border-color: rgba(48, 216, 255, 0.55);
    background: rgba(48, 216, 255, 0.16);
    color: rgba(48, 216, 255, 0.95);
  }

  .rrm-queueError {
    width: 100%;
    text-align: right;
    font-size: 0.74rem;
    color: rgba(255, 149, 149, 0.95);
  }

  .rrm-tableWrap {
    --rrm-col-1: 180px;
    --rrm-col-2: 190px;
    --rrm-col-3: 130px;
    --rrm-col-4: 160px;
    --rrm-day-col: 84px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    padding: 0 10px 14px;
    scrollbar-gutter: stable both-edges;
    position: relative;
  }

  .rrm-table {
    width: max-content;
    min-width: 100%;
    table-layout: fixed;
    border-collapse: separate;
    border-spacing: 0;
    color: rgba(230, 230, 247, 0.94);
  }

  .rrm-table thead {
    position: relative;
    z-index: 9;
  }

  .rrm-table thead th {
    position: sticky;
    top: 0;
    z-index: 3;
    background: linear-gradient(180deg, rgba(22, 22, 50, 0.98), rgba(12, 12, 28, 0.96));
    border-bottom: none !important;
    box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.05);
    padding: 7px 6px;
    text-align: center;
    vertical-align: middle;
    font-size: 0.75rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .rrm-table th.rrm-headCell {
    padding: 0 !important;
  }

  .rrm-thBtn {
    position: relative;
    width: 100%;
    border: none;
    background: transparent;
    padding: 8px 18px 8px 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 2px;
    text-align: left;
    cursor: pointer;
    user-select: none;
    color: rgba(255, 255, 255, 0.92);
    transition: background 120ms ease, transform 120ms ease;
    min-height: 48px;
  }

  .rrm-thBtn:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  .rrm-thBtn:active {
    transform: translateY(0.5px);
  }

  .rrm-thBtn:focus-visible {
    outline: 2px solid rgba(48, 216, 255, 0.7);
    outline-offset: -2px;
  }

  .rrm-thLabel {
    display: block;
    width: 100%;
    min-width: 0;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    font-weight: 900;
    font-size: 0.64rem;
    letter-spacing: 0.04em;
    text-transform: none;
    color: rgba(255, 255, 255, 0.92);
  }

  .rrm-thSub {
    display: block;
    width: 100%;
    min-width: 0;
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    font-weight: 750;
    font-size: 0.62rem;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.78);
    text-transform: none;
  }

  .rrm-thDot {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: rgba(48, 216, 255, 0);
    box-shadow: 0 0 0 0 rgba(48, 216, 255, 0);
    transition: background 120ms ease, box-shadow 120ms ease;
  }

  .rrm-thDot--on {
    background: rgba(48, 216, 255, 0.95);
    box-shadow: 0 0 0 4px rgba(48, 216, 255, 0.14);
  }

  .rrm-thBtn--filtered {
    background: rgba(0, 0, 0, 0.1);
  }

  .rrm-thBtn--open {
    background: rgba(255, 255, 255, 0.08);
  }

  .rrm-table tbody tr {
    height: 66px;
    min-height: 66px;
    max-height: 66px;
    background: rgba(255, 255, 255, 0.01);
  }

  .rrm-table tbody tr:nth-child(even) {
    background: rgba(255, 255, 255, 0.025);
  }

  .rrm-table tbody td {
    height: 66px;
    min-height: 66px;
    max-height: 66px;
    padding: 5px 6px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.03);
    vertical-align: middle;
  }

  .rrm-sticky {
    position: sticky;
    z-index: 3;
    background: rgba(14, 14, 32, 0.97);
  }

  .rrm-table thead .rrm-sticky {
    z-index: 12;
    background: linear-gradient(180deg, rgba(22, 22, 50, 0.98), rgba(12, 12, 28, 0.98));
  }

  .rrm-sticky.s1 {
    left: 0;
    width: var(--rrm-col-1);
    min-width: var(--rrm-col-1);
    max-width: var(--rrm-col-1);
  }

  .rrm-sticky.s2 {
    left: var(--rrm-col-1);
    width: var(--rrm-col-2);
    min-width: var(--rrm-col-2);
    max-width: var(--rrm-col-2);
  }

  .rrm-sticky.s3 {
    left: calc(var(--rrm-col-1) + var(--rrm-col-2));
    width: var(--rrm-col-3);
    min-width: var(--rrm-col-3);
    max-width: var(--rrm-col-3);
  }

  .rrm-sticky.s4 {
    left: calc(var(--rrm-col-1) + var(--rrm-col-2) + var(--rrm-col-3));
    width: var(--rrm-col-4);
    min-width: var(--rrm-col-4);
    max-width: var(--rrm-col-4);
  }

  .rrm-stickyShadow {
    box-shadow:
      inset -1px 0 0 rgba(255, 255, 255, 0.04),
      8px 0 14px rgba(0, 0, 0, 0.18);
  }

  .rrm-metaCell {
    text-align: left;
  }

  .rrm-metaText {
    max-height: 54px;
    overflow: auto;
    padding-right: 2px;
    line-height: 1.18;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    font-size: 0.78rem;
    color: rgba(230, 230, 247, 0.92);
  }

  .rrm-rateLink {
    display: block;
    max-height: 54px;
    overflow: auto;
    border: 0;
    background: transparent;
    color: rgba(255, 201, 76, 0.92);
    cursor: pointer;
    padding: 0;
    padding-right: 2px;
    margin: 0;
    width: 100%;
    text-align: left;
    font-size: 0.79rem;
    line-height: 1.18;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .rrm-rateLink:hover {
    color: rgba(255, 216, 125, 0.95);
    text-decoration: underline;
  }

  .rrm-dayHeader {
    width: var(--rrm-day-col);
    min-width: var(--rrm-day-col);
    max-width: var(--rrm-day-col);
  }

  .rrm-dayHeaderMain {
    font-size: 0.72rem;
    font-weight: 800;
    color: rgba(230, 230, 247, 0.94);
    line-height: 1.1;
  }

  .rrm-dayHeaderSub {
    margin-top: 2px;
    font-size: 0.62rem;
    color: rgba(230, 230, 247, 0.62);
    line-height: 1;
  }

  .rrm-filterPopover {
    position: fixed;
    z-index: 3000;
    width: min(380px, calc(100vw - 24px));
    border-radius: 16px;
    border: 1px solid transparent;
    background:
      linear-gradient(180deg, rgba(25, 25, 51, 0.96), rgba(10, 10, 22, 0.96)) padding-box,
      linear-gradient(135deg, rgba(48, 216, 255, 0.45), rgba(168, 85, 247, 0.5), rgba(255, 77, 79, 0.28)) border-box;
    box-shadow: 0 28px 90px rgba(0, 0, 0, 0.6);
    overflow: hidden;
  }

  .rrm-filterHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 12px 10px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);
  }

  .rrm-filterTitle {
    font-weight: 900;
    color: rgba(230, 230, 247, 0.92);
    font-size: 0.95rem;
    letter-spacing: 0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rrm-filterHeaderActions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
  }

  .rrm-filterSmallBtn {
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    color: rgba(230, 230, 247, 0.86);
    padding: 7px 10px;
    border-radius: 12px;
    font-weight: 800;
    font-size: 0.82rem;
    cursor: pointer;
    transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
  }

  .rrm-filterSmallBtn:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(48, 216, 255, 0.28);
  }

  .rrm-filterCloseBtn {
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.04);
    color: rgba(230, 230, 247, 0.86);
    width: 34px;
    height: 34px;
    border-radius: 12px;
    cursor: pointer;
    font-size: 1.05rem;
    line-height: 1;
    display: grid;
    place-items: center;
    transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
  }

  .rrm-filterCloseBtn:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 77, 79, 0.35);
  }

  .rrm-filterBody {
    padding: 12px;
  }

  .rrm-filterInput {
    width: 100%;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(230, 230, 247, 0.92);
    border-radius: 12px;
    padding: 10px 12px;
    outline: none;
    transition: border-color 120ms ease, box-shadow 120ms ease, background 120ms ease;
  }

  .rrm-filterInput::placeholder {
    color: rgba(230, 230, 247, 0.45);
  }

  .rrm-filterInput:focus {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(48, 216, 255, 0.55);
    box-shadow: 0 0 0 4px rgba(48, 216, 255, 0.12);
  }

  .rrm-filterHint {
    margin-top: 8px;
    font-size: 0.82rem;
    color: rgba(230, 230, 247, 0.62);
  }

  .rrm-filterList {
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: min(320px, 55vh);
    overflow: auto;
    overscroll-behavior: contain;
    padding-right: 2px;
  }

  .rrm-filterOption {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);
    cursor: pointer;
    transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
    user-select: none;
  }

  .rrm-filterOption:hover {
    background: rgba(48, 216, 255, 0.05);
    border-color: rgba(48, 216, 255, 0.16);
    transform: translateY(-1px);
  }

  .rrm-filterOption input {
    accent-color: rgba(48, 216, 255, 0.95);
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  .rrm-filterOptionText {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .rrm-filterOptionLabel {
    font-weight: 850;
    color: rgba(230, 230, 247, 0.92);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rrm-filterOptionMeta {
    font-size: 0.78rem;
    color: rgba(230, 230, 247, 0.62);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .rrm-filterCountPill {
    flex: 0 0 auto;
    padding: 4px 10px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.03);
    color: rgba(230, 230, 247, 0.78);
    font-variant-numeric: tabular-nums;
  }

  .rrm-priceCell {
    width: var(--rrm-day-col);
    min-width: var(--rrm-day-col);
    max-width: var(--rrm-day-col);
    text-align: center;
  }

  .rrm-priceCellInner {
    position: relative;
    height: 54px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.10);
    background: rgba(255, 255, 255, 0.03);
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2px;
    cursor: pointer;
    padding: 4px;
    user-select: none;
  }

  .rrm-priceCellInner.muted {
    opacity: 0.6;
  }

  .rrm-priceCellInner.stopSell {
    background: rgba(255, 90, 90, 0.18);
    border-color: rgba(255, 120, 120, 0.45);
  }

  .rrm-priceCellInner.automaticStopSell {
    background: rgba(255, 184, 77, 0.18);
    border-color: rgba(255, 198, 95, 0.5);
  }

  .rrm-priceCellInner.pending {
    border-color: rgba(48, 216, 255, 0.58);
    box-shadow: inset 0 0 0 1px rgba(48, 216, 255, 0.22);
  }

  .rrm-stopSellToggle {
    position: absolute;
    top: 3px;
    right: 3px;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    border: 1px solid rgba(255, 166, 166, 0.44);
    background: linear-gradient(180deg, rgba(160, 38, 38, 0.96), rgba(112, 18, 24, 0.96));
    color: rgba(255, 246, 246, 0.98);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    font-size: 0.46rem;
    font-weight: 900;
    line-height: 1;
    cursor: pointer;
    opacity: 0.94;
    box-shadow:
      0 5px 12px rgba(0, 0, 0, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.18);
    backdrop-filter: blur(6px);
    transition:
      transform 120ms ease,
      background 120ms ease,
      border-color 120ms ease,
      color 120ms ease,
      box-shadow 120ms ease,
      opacity 120ms ease;
  }

  .rrm-priceCellInner:hover .rrm-stopSellToggle,
  .rrm-stopSellToggle:focus-visible {
    opacity: 0.96;
  }

  .rrm-stopSellToggle:hover:not(:disabled) {
    transform: translateY(-1px) scale(1.05);
    background: linear-gradient(180deg, rgba(185, 44, 44, 0.98), rgba(133, 22, 29, 0.98));
    border-color: rgba(255, 211, 211, 0.58);
    color: #ffffff;
  }

  .rrm-stopSellToggle:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(48, 216, 255, 0.14);
  }

  .rrm-stopSellToggle.isActive {
    background: linear-gradient(180deg, rgba(245, 110, 110, 0.98), rgba(214, 70, 82, 0.98));
    border-color: rgba(255, 231, 231, 0.72);
    color: #ffffff;
    box-shadow:
      0 7px 16px rgba(214, 70, 82, 0.32),
      inset 0 1px 0 rgba(255, 255, 255, 0.28);
    opacity: 1;
  }

  .rrm-stopSellToggle.isAutomatic {
    background: linear-gradient(180deg, rgba(245, 158, 11, 0.98), rgba(180, 83, 9, 0.98));
    border-color: rgba(255, 228, 181, 0.72);
    color: #fffaf0;
  }

  .rrm-stopSellToggle:disabled {
    cursor: progress;
    transform: none;
  }

  .rrm-priceValue {
    font-size: 0.79rem;
    font-weight: 800;
    line-height: 1;
    color: rgba(230, 230, 247, 0.95);
  }

  .rrm-constraintRow {
    min-height: 11px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    font-size: 0.56rem;
    line-height: 1;
    color: rgba(230, 230, 247, 0.72);
    white-space: nowrap;
  }

  .rrm-constraintBadge {
    padding: 1px 3px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.30);
  }

  .rrm-priceInput {
    width: 100%;
    border-radius: 8px;
    border: 1px solid rgba(48, 216, 255, 0.6);
    background: rgba(0, 0, 0, 0.45);
    color: rgba(230, 230, 247, 0.96);
    font-size: 0.76rem;
    text-align: center;
    padding: 4px 3px;
    height: 28px;
    -moz-appearance: textfield;
  }

  .rrm-priceInput:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(48, 216, 255, 0.16);
  }

  .rrm-priceInput::-webkit-outer-spin-button,
  .rrm-priceInput::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .rrm-state {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    padding: 30px 14px;
    color: rgba(230, 230, 247, 0.84);
  }

  .rrm-stateTitle {
    font-size: 1rem;
    font-weight: 700;
  }

  .rrm-stateSubtitle {
    font-size: 0.84rem;
    color: rgba(230, 230, 247, 0.64);
  }

  .rrm-page--light {
    color: #183645;
  }

  .rrm-page--light .rrm-card {
    border-color: rgba(24, 54, 69, 0.12);
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(246, 240, 232, 0.97)) padding-box,
      linear-gradient(140deg, rgba(24, 54, 69, 0.14), rgba(191, 149, 100, 0.2)) border-box;
    box-shadow: 0 24px 56px rgba(86, 65, 39, 0.12);
  }

  .rrm-page--light .rrm-card::before {
    background:
      radial-gradient(900px 320px at 10% 0%, rgba(24, 54, 69, 0.08), transparent 60%),
      radial-gradient(700px 260px at 92% 8%, rgba(191, 149, 100, 0.14), transparent 60%),
      radial-gradient(820px 240px at 36% 112%, rgba(128, 94, 54, 0.08), transparent 60%);
    opacity: 1;
  }

  .rrm-page--light .rrm-cardHeader {
    border-bottom-color: rgba(24, 54, 69, 0.1);
  }

  .rrm-page--light .rrm-title {
    background: linear-gradient(90deg, #214150 0%, #486575 54%, #9c7343 100%);
    -webkit-background-clip: text;
    background-clip: text;
    text-shadow: none;
  }

  .rrm-page--light .rrm-subtitle {
    color: #5b6d79;
  }

  .rrm-page--light .rrm-label {
    color: #627480;
  }

  .rrm-page--light .rrm-input {
    border-color: rgba(24, 54, 69, 0.12);
    background: rgba(255, 255, 255, 0.92);
    color: #183645;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
  }

  .rrm-page--light .rrm-input:focus {
    border-color: rgba(24, 54, 69, 0.24);
    box-shadow: 0 0 0 4px rgba(24, 54, 69, 0.08);
  }

  .rrm-page--light .rrm-btn {
    color: #1d3a48;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 242, 233, 0.96));
    border-color: rgba(93, 71, 43, 0.16);
    box-shadow: 0 10px 20px rgba(86, 65, 39, 0.08);
  }

  .rrm-page--light .rrm-btn:hover {
    box-shadow: 0 14px 24px rgba(86, 65, 39, 0.12);
  }

  .rrm-page--light .rrm-btn--primary {
    color: #fffaf1;
    border-color: rgba(128, 94, 54, 0.34);
    background: linear-gradient(135deg, #7f5c39 0%, #a87a48 54%, #bf9564 100%);
    box-shadow: 0 16px 30px rgba(128, 94, 54, 0.2);
  }

  .rrm-page--light .rrm-btn--primary:hover {
    background: linear-gradient(135deg, #896642 0%, #b48653 54%, #cda272 100%);
    box-shadow: 0 18px 34px rgba(128, 94, 54, 0.24);
  }

  .rrm-page--light .rrm-btn--green {
    color: #f4fff9;
    border-color: rgba(22, 101, 52, 0.28);
    background: linear-gradient(135deg, #15803d 0%, #0f9f7a 100%);
    box-shadow: 0 16px 28px rgba(21, 128, 61, 0.16);
  }

  .rrm-page--light .rrm-btn--amber {
    color: #fffaf1;
    border-color: rgba(146, 64, 14, 0.28);
    background: linear-gradient(135deg, #b45309 0%, #d97706 100%);
    box-shadow: 0 16px 28px rgba(180, 83, 9, 0.16);
  }

  .rrm-page--light .rrm-queueBadge {
    border-color: rgba(24, 54, 69, 0.12);
    color: #55707f;
    background: rgba(255, 255, 255, 0.72);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.74);
  }

  .rrm-page--light .rrm-queueBadge.isActive {
    border-color: rgba(128, 94, 54, 0.26);
    background: rgba(191, 149, 100, 0.16);
    color: #7f5c39;
  }

  .rrm-page--light .rrm-queueError {
    color: #a61b4f;
  }

  .rrm-page--light .rrm-tableWrap {
    border-radius: 18px;
    border: 1px solid rgba(24, 54, 69, 0.12);
    background:
      linear-gradient(180deg, rgba(255, 252, 248, 0.98), rgba(246, 240, 232, 0.98)),
      radial-gradient(760px 180px at 8% 0%, rgba(24, 54, 69, 0.06), transparent 66%);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.86), 0 20px 36px rgba(86, 65, 39, 0.08);
    scrollbar-width: thin;
    scrollbar-color: rgba(117, 140, 154, 0.78) rgba(24, 54, 69, 0.08);
  }

  .rrm-page--light .rrm-tableWrap::-webkit-scrollbar {
    width: 7px;
    height: 7px;
  }

  .rrm-page--light .rrm-tableWrap::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(117, 140, 154, 0.9), rgba(74, 96, 111, 0.9));
    border-radius: 999px;
  }

  .rrm-page--light .rrm-tableWrap::-webkit-scrollbar-track {
    background: rgba(24, 54, 69, 0.08);
    border-radius: 999px;
  }

  .rrm-page--light .rrm-table thead {
    background: transparent !important;
    background-color: transparent !important;
  }

  .rrm-page--light .rrm-table thead th {
    background: linear-gradient(180deg, rgba(251, 247, 241, 0.99), rgba(239, 232, 223, 0.99)) !important;
    background-color: rgba(245, 238, 229, 0.99) !important;
    color: #28424f !important;
    box-shadow:
      inset -1px 0 0 rgba(128, 94, 54, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.82);
  }

  .rrm-page--light .rrm-sticky {
    background: rgba(249, 245, 239, 0.98);
  }

  .rrm-page--light .rrm-table thead .rrm-sticky {
    background: linear-gradient(180deg, rgba(251, 247, 241, 0.99), rgba(239, 232, 223, 0.99)) !important;
    background-color: rgba(245, 238, 229, 0.99) !important;
  }

  .rrm-page--light .rrm-stickyShadow {
    box-shadow:
      inset -1px 0 0 rgba(24, 54, 69, 0.06),
      10px 0 18px rgba(86, 65, 39, 0.08);
  }

  .rrm-page--light .rrm-thBtn {
    color: #214150;
  }

  .rrm-page--light .rrm-thBtn:hover {
    background: rgba(24, 54, 69, 0.05);
  }

  .rrm-page--light .rrm-thBtn:focus-visible {
    outline-color: rgba(128, 94, 54, 0.5);
  }

  .rrm-page--light .rrm-thBtn--filtered {
    background: rgba(24, 54, 69, 0.06);
  }

  .rrm-page--light .rrm-thBtn--open {
    background: rgba(191, 149, 100, 0.16);
  }

  .rrm-page--light .rrm-thLabel {
    color: #214150;
  }

  .rrm-page--light .rrm-thSub {
    color: #627480;
  }

  .rrm-page--light .rrm-thDot {
    background: rgba(128, 94, 54, 0);
    box-shadow: 0 0 0 0 rgba(128, 94, 54, 0);
  }

  .rrm-page--light .rrm-thDot--on {
    background: #9c7343;
    box-shadow: 0 0 0 4px rgba(191, 149, 100, 0.18);
  }

  .rrm-page--light .rrm-table tbody tr {
    background: rgba(255, 255, 255, 0.55);
  }

  .rrm-page--light .rrm-table tbody tr:nth-child(even) {
    background: rgba(247, 242, 234, 0.92);
  }

  .rrm-page--light .rrm-table tbody td {
    border-bottom-color: rgba(24, 54, 69, 0.08);
    box-shadow: inset -1px 0 0 rgba(24, 54, 69, 0.05);
  }

  .rrm-page--light .rrm-metaText {
    color: #1f3b49;
  }

  .rrm-page--light .rrm-rateLink {
    color: #8d6133;
  }

  .rrm-page--light .rrm-rateLink:hover {
    color: #b17a44;
  }

  .rrm-page--light .rrm-dayHeaderMain {
    color: #214150;
  }

  .rrm-page--light .rrm-dayHeaderSub {
    color: #687985;
  }

  .rrm-page--light .rrm-filterPopover {
    border-color: rgba(24, 54, 69, 0.12);
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.99), rgba(246, 240, 232, 0.97)) padding-box,
      linear-gradient(140deg, rgba(24, 54, 69, 0.14), rgba(191, 149, 100, 0.18)) border-box;
    box-shadow: 0 24px 56px rgba(86, 65, 39, 0.16);
  }

  .rrm-page--light .rrm-filterHeader {
    border-bottom-color: rgba(24, 54, 69, 0.08);
    background: rgba(255, 255, 255, 0.5);
  }

  .rrm-page--light .rrm-filterTitle {
    color: #214150;
  }

  .rrm-page--light .rrm-filterSmallBtn,
  .rrm-page--light .rrm-filterCloseBtn {
    border-color: rgba(24, 54, 69, 0.12);
    background: rgba(255, 255, 255, 0.8);
    color: #214150;
  }

  .rrm-page--light .rrm-filterSmallBtn:hover,
  .rrm-page--light .rrm-filterCloseBtn:hover {
    background: rgba(191, 149, 100, 0.12);
    border-color: rgba(128, 94, 54, 0.22);
  }

  .rrm-page--light .rrm-filterInput {
    background: rgba(255, 255, 255, 0.92);
    border-color: rgba(24, 54, 69, 0.12);
    color: #183645;
  }

  .rrm-page--light .rrm-filterInput::placeholder {
    color: #8a99a3;
  }

  .rrm-page--light .rrm-filterInput:focus {
    background: rgba(255, 255, 255, 0.98);
    border-color: rgba(24, 54, 69, 0.24);
    box-shadow: 0 0 0 4px rgba(24, 54, 69, 0.08);
  }

  .rrm-page--light .rrm-filterHint {
    color: #677986;
  }

  .rrm-page--light .rrm-filterOption {
    border-color: rgba(24, 54, 69, 0.08);
    background: rgba(255, 255, 255, 0.56);
  }

  .rrm-page--light .rrm-filterOption:hover {
    background: rgba(233, 228, 221, 0.86);
    border-color: rgba(128, 94, 54, 0.16);
  }

  .rrm-page--light .rrm-filterOption input {
    accent-color: #7f5c39;
  }

  .rrm-page--light .rrm-filterOptionLabel {
    color: #214150;
  }

  .rrm-page--light .rrm-filterOptionMeta {
    color: #687985;
  }

  .rrm-page--light .rrm-filterCountPill {
    border-color: rgba(24, 54, 69, 0.1);
    background: rgba(255, 255, 255, 0.78);
    color: #617380;
  }

  .rrm-page--light .rrm-priceCellInner {
    border-color: rgba(24, 54, 69, 0.12);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(246, 240, 232, 0.9));
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
  }

  .rrm-page--light .rrm-priceCellInner.stopSell {
    background: rgba(205, 115, 105, 0.16);
    border-color: rgba(166, 75, 68, 0.34);
  }

  .rrm-page--light .rrm-priceCellInner.automaticStopSell {
    background: rgba(191, 149, 100, 0.2);
    border-color: rgba(128, 94, 54, 0.38);
  }

  .rrm-page--light .rrm-priceCellInner.pending {
    border-color: rgba(128, 94, 54, 0.4);
    box-shadow:
      inset 0 0 0 1px rgba(191, 149, 100, 0.24),
      0 10px 20px rgba(128, 94, 54, 0.08);
  }

  .rrm-page--light .rrm-stopSellToggle {
    border-color: rgba(154, 73, 53, 0.42);
    background: linear-gradient(180deg, rgba(201, 116, 86, 0.96), rgba(164, 76, 58, 0.96));
    color: #fffaf7;
    box-shadow:
      0 5px 12px rgba(111, 77, 47, 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.22);
  }

  .rrm-page--light .rrm-stopSellToggle:hover:not(:disabled) {
    background: linear-gradient(180deg, rgba(214, 128, 96, 0.98), rgba(176, 82, 61, 0.98));
    border-color: rgba(154, 73, 53, 0.58);
    color: #ffffff;
  }

  .rrm-page--light .rrm-stopSellToggle:focus-visible {
    box-shadow: 0 0 0 2px rgba(24, 54, 69, 0.12);
  }

  .rrm-page--light .rrm-stopSellToggle.isActive {
    background: linear-gradient(180deg, rgba(185, 88, 68, 0.98), rgba(149, 61, 49, 0.98));
    border-color: rgba(121, 47, 39, 0.62);
    color: #fffdfb;
    box-shadow:
      0 7px 16px rgba(149, 61, 49, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.22);
  }

  .rrm-page--light .rrm-stopSellToggle.isAutomatic {
    background: linear-gradient(180deg, rgba(191, 149, 100, 0.98), rgba(128, 94, 54, 0.98));
    border-color: rgba(100, 73, 45, 0.62);
    color: #fffdf8;
  }

  .rrm-page--light .rrm-priceValue {
    color: #1f3b49;
  }

  .rrm-page--light .rrm-constraintRow {
    color: #687985;
  }

  .rrm-page--light .rrm-constraintBadge {
    background: rgba(24, 54, 69, 0.08);
    color: #49606e;
  }

  .rrm-page--light .rrm-priceInput {
    border-color: rgba(128, 94, 54, 0.28);
    background: rgba(255, 255, 255, 0.95);
    color: #183645;
  }

  .rrm-page--light .rrm-priceInput:focus {
    box-shadow: 0 0 0 3px rgba(191, 149, 100, 0.14);
  }

  .rrm-page--light .rrm-state {
    color: #214150;
  }

  .rrm-page--light .rrm-stateSubtitle {
    color: #677986;
  }

  .rrm-error {
    color: rgba(255, 149, 149, 0.95);
    font-size: 0.82rem;
  }

  .rrm-page--light .rrm-error {
    color: #a61b4f;
  }

  /* --- Drag selection highlight --- */
  .rrm-priceCellInner.dragSelected {
    border-color: rgba(48, 216, 255, 0.75);
    background: rgba(48, 216, 255, 0.13);
    box-shadow: inset 0 0 0 1px rgba(48, 216, 255, 0.28);
  }

  .rrm-page--light .rrm-priceCellInner.dragSelected {
    border-color: rgba(128, 94, 54, 0.55);
    background: rgba(191, 149, 100, 0.14);
    box-shadow: inset 0 0 0 1px rgba(191, 149, 100, 0.24);
  }

  /* --- Cell Edit Modal --- */
  .rrm-cellModal {
    position: fixed;
    z-index: 20000;
    background: rgba(10, 10, 22, 0.98);
    border: 1px solid rgba(48, 216, 255, 0.36);
    border-radius: 12px;
    padding: 10px 10px 12px;
    box-shadow:
      0 10px 32px rgba(0, 0, 0, 0.65),
      0 2px 8px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.06);
    display: flex;
    flex-direction: column;
    gap: 7px;
    width: 148px;
    backdrop-filter: blur(14px);
  }

  .rrm-cellModal-count {
    font-size: 0.6rem;
    color: rgba(48, 216, 255, 0.72);
    text-align: center;
    font-weight: 800;
    padding-bottom: 5px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    margin-bottom: 1px;
  }

  .rrm-cellModal-field {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .rrm-cellModal-label {
    font-size: 0.59rem;
    color: rgba(230, 230, 247, 0.52);
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding-left: 2px;
  }

  .rrm-cellModal-input {
    width: 100%;
    border-radius: 7px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.06);
    color: rgba(230, 230, 247, 0.96);
    font-size: 0.84rem;
    font-weight: 700;
    text-align: center;
    padding: 4px 6px;
    height: 28px;
    -moz-appearance: textfield;
    transition: border-color 120ms ease, background 120ms ease;
  }

  .rrm-cellModal-input:focus {
    outline: none;
    border-color: rgba(48, 216, 255, 0.58);
    background: rgba(48, 216, 255, 0.07);
  }

  .rrm-cellModal-input::-webkit-outer-spin-button,
  .rrm-cellModal-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .rrm-page--light .rrm-cellModal {
    background: rgba(255, 252, 248, 0.99);
    border-color: rgba(128, 94, 54, 0.24);
    box-shadow:
      0 10px 32px rgba(86, 65, 39, 0.18),
      0 2px 8px rgba(86, 65, 39, 0.10);
  }

  .rrm-page--light .rrm-cellModal-count {
    color: rgba(128, 94, 54, 0.78);
    border-bottom-color: rgba(24, 54, 69, 0.08);
  }

  .rrm-page--light .rrm-cellModal-label {
    color: rgba(24, 54, 69, 0.52);
  }

  .rrm-page--light .rrm-cellModal-input {
    border-color: rgba(24, 54, 69, 0.16);
    background: rgba(255, 255, 255, 0.9);
    color: #183645;
  }

  .rrm-page--light .rrm-cellModal-input:focus {
    border-color: rgba(128, 94, 54, 0.36);
    background: rgba(255, 250, 240, 0.96);
  }

  .rrm-cellModal-actions {
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 2px;
  }

  .rrm-cellModal-btn {
    width: 26px;
    height: 26px;
    border-radius: 999px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.78rem;
    font-weight: 900;
    line-height: 1;
    transition: transform 120ms ease, opacity 120ms ease;
    opacity: 0.82;
  }

  .rrm-cellModal-btn:hover {
    transform: scale(1.12);
    opacity: 1;
  }

  .rrm-cellModal-btn--confirm {
    background: rgba(52, 199, 89, 0.18);
    color: rgba(52, 199, 89, 1);
    box-shadow: 0 0 0 1px rgba(52, 199, 89, 0.3);
  }

  .rrm-cellModal-btn--cancel {
    background: rgba(255, 69, 58, 0.15);
    color: rgba(255, 69, 58, 1);
    box-shadow: 0 0 0 1px rgba(255, 69, 58, 0.28);
  }

  .rrm-page--light .rrm-cellModal-btn--confirm {
    background: rgba(52, 199, 89, 0.12);
    color: rgba(30, 140, 60, 1);
    box-shadow: 0 0 0 1px rgba(52, 199, 89, 0.25);
  }

  .rrm-page--light .rrm-cellModal-btn--cancel {
    background: rgba(255, 69, 58, 0.10);
    color: rgba(200, 40, 30, 1);
    box-shadow: 0 0 0 1px rgba(255, 69, 58, 0.22);
  }

  .rrm-stopSellNoticeOverlay {
    position: fixed;
    inset: 0;
    z-index: 21000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(5, 5, 12, 0.58);
    backdrop-filter: blur(7px);
  }

  .rrm-stopSellNotice {
    width: min(480px, 100%);
    border-radius: 18px;
    border: 1px solid rgba(255, 149, 149, 0.34);
    background:
      radial-gradient(circle at top left, rgba(255, 149, 149, 0.16), transparent 38%),
      rgba(12, 12, 28, 0.98);
    box-shadow:
      0 22px 70px rgba(0, 0, 0, 0.58),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
    padding: 18px;
    color: rgba(245, 245, 255, 0.95);
  }

  .rrm-stopSellNoticeTitle {
    font-size: 1rem;
    font-weight: 900;
    margin-bottom: 7px;
  }

  .rrm-stopSellNoticeIntro {
    font-size: 0.82rem;
    color: rgba(230, 230, 247, 0.72);
    line-height: 1.45;
    margin-bottom: 12px;
  }

  .rrm-stopSellNoticeList {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 260px;
    overflow: auto;
    margin-bottom: 16px;
  }

  .rrm-stopSellNoticeItem {
    border-radius: 12px;
    border: 1px solid rgba(255, 149, 149, 0.18);
    background: rgba(255, 255, 255, 0.055);
    padding: 9px 10px;
  }

  .rrm-stopSellNoticeMain {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    font-size: 0.82rem;
    font-weight: 800;
  }

  .rrm-stopSellNoticeAction {
    color: rgba(255, 192, 120, 0.95);
    white-space: nowrap;
  }

  .rrm-stopSellNoticeRate {
    margin-top: 4px;
    font-size: 0.74rem;
    color: rgba(230, 230, 247, 0.62);
  }

  .rrm-stopSellNoticeButton {
    width: 100%;
    border: none;
    border-radius: 999px;
    padding: 10px 14px;
    cursor: pointer;
    font-weight: 900;
    color: rgba(10, 10, 22, 0.96);
    background: linear-gradient(135deg, rgba(255, 192, 120, 1), rgba(255, 149, 149, 1));
    box-shadow: 0 10px 28px rgba(255, 149, 149, 0.22);
  }

  .rrm-page--light .rrm-stopSellNoticeOverlay {
    background: rgba(24, 54, 69, 0.22);
  }

  .rrm-page--light .rrm-stopSellNotice {
    background:
      radial-gradient(circle at top left, rgba(200, 40, 30, 0.10), transparent 38%),
      rgba(255, 252, 248, 0.98);
    border-color: rgba(200, 40, 30, 0.2);
    color: #183645;
    box-shadow: 0 22px 70px rgba(86, 65, 39, 0.22);
  }

  .rrm-page--light .rrm-stopSellNoticeIntro,
  .rrm-page--light .rrm-stopSellNoticeRate {
    color: rgba(24, 54, 69, 0.62);
  }

  .rrm-page--light .rrm-stopSellNoticeItem {
    background: rgba(255, 255, 255, 0.86);
    border-color: rgba(200, 40, 30, 0.14);
  }

  .rrm-page--light .rrm-stopSellNoticeAction {
    color: rgba(160, 85, 28, 1);
  }
`;

const pilcoreBrandCss = `
  /*
   * Pilcore skin: intentionally limited to colour, typography and control chrome.
   * Matrix geometry, sticky positioning and scroll ownership stay in uiCss.
   */
  .rrm-page {
    --rrm-brand: #223a7a;
    --rrm-brand-hover: #1b2f67;
    --rrm-horizon: #3db6e9;
    --rrm-connect: #63c1c7;
    --rrm-ink: #f2f6ff;
    --rrm-text: #dfe8f7;
    --rrm-muted: #aebbd0;
    --rrm-surface: #0e1730;
    --rrm-surface-soft: #121f3d;
    --rrm-surface-raised: #152344;
    --rrm-border: rgba(99, 193, 199, 0.24);
    --rrm-border-strong: rgba(61, 182, 233, 0.58);
    --rrm-row-even: #111d39;
    --rrm-row-hover: #172a4d;
    --rrm-positive: #a9e1da;
    --rrm-positive-bg: #143d42;
    --rrm-positive-border: #397d7e;
    --rrm-danger: #ffc7cf;
    --rrm-danger-bg: #3a1d2a;
    --rrm-danger-border: #8d4051;
    color: var(--rrm-text);
    color-scheme: dark;
    font-family: "Montserrat", sans-serif;
  }

  .rrm-page--light {
    --rrm-ink: #172653;
    --rrm-text: #26354f;
    --rrm-muted: #5b6680;
    --rrm-surface: #ffffff;
    --rrm-surface-soft: #f4f8fb;
    --rrm-surface-raised: #ffffff;
    --rrm-border: #d7e2ec;
    --rrm-border-strong: #7c90a7;
    --rrm-row-even: #f7fafc;
    --rrm-row-hover: #eaf7fc;
    --rrm-positive: #216b5a;
    --rrm-positive-bg: #e7f6f2;
    --rrm-positive-border: #9fd5cb;
    --rrm-danger: #9b3445;
    --rrm-danger-bg: #fdeff1;
    --rrm-danger-border: #e8aeb8;
    color: var(--rrm-text);
    color-scheme: light;
  }

  .rrm-card,
  .rrm-page--light .rrm-card {
    border: 1px solid var(--rrm-border);
    border-top: 3px solid var(--rrm-horizon);
    border-radius: 16px;
    background: var(--rrm-surface);
    box-shadow: 0 10px 28px rgba(34, 58, 122, 0.1);
  }

  .rrm-page:not(.rrm-page--light) .rrm-card {
    box-shadow: 0 18px 44px rgba(2, 8, 28, 0.34);
  }

  .rrm-card::before,
  .rrm-page--light .rrm-card::before {
    display: none;
  }

  .rrm-cardHeader,
  .rrm-page--light .rrm-cardHeader {
    border-bottom-color: var(--rrm-border);
    background: var(--rrm-surface);
  }

  .rrm-title,
  .rrm-page--light .rrm-title {
    color: var(--rrm-ink);
    background: none;
    -webkit-background-clip: border-box;
    background-clip: border-box;
    -webkit-text-fill-color: currentColor;
    text-shadow: none;
    font-weight: 800;
    letter-spacing: -0.025em;
  }

  .rrm-subtitle,
  .rrm-page--light .rrm-subtitle,
  .rrm-label,
  .rrm-page--light .rrm-label {
    color: var(--rrm-muted);
  }

  .rrm-input,
  .rrm-page--light .rrm-input,
  .rrm-filterInput,
  .rrm-page--light .rrm-filterInput,
  .rrm-cellModal-input,
  .rrm-page--light .rrm-cellModal-input {
    border-color: var(--rrm-border-strong);
    color: var(--rrm-text);
    background: var(--rrm-surface-raised);
    box-shadow: none;
    font-family: inherit;
  }

  .rrm-input:focus,
  .rrm-page--light .rrm-input:focus,
  .rrm-filterInput:focus,
  .rrm-page--light .rrm-filterInput:focus,
  .rrm-cellModal-input:focus,
  .rrm-page--light .rrm-cellModal-input:focus {
    outline: 2px solid var(--rrm-brand);
    outline-offset: 2px;
    border-color: var(--rrm-horizon);
    background: var(--rrm-surface-raised);
    box-shadow: 0 0 0 3px rgba(61, 182, 233, 0.22);
  }

  .rrm-btn,
  .rrm-page--light .rrm-btn {
    border-color: var(--rrm-border-strong);
    color: var(--rrm-text);
    background: var(--rrm-surface-raised);
    box-shadow: none;
    font-family: inherit;
  }

  .rrm-btn:hover:not(:disabled),
  .rrm-page--light .rrm-btn:hover:not(:disabled) {
    border-color: var(--rrm-horizon);
    color: var(--rrm-ink);
    background: var(--rrm-row-hover);
    box-shadow: none;
  }

  .rrm-btn:focus-visible,
  .rrm-filterSmallBtn:focus-visible,
  .rrm-filterCloseBtn:focus-visible,
  .rrm-stopSellNoticeButton:focus-visible,
  .rrm-cellModal-btn:focus-visible {
    outline: 2px solid var(--rrm-brand);
    outline-offset: 2px;
    box-shadow: 0 0 0 3px rgba(61, 182, 233, 0.22);
  }

  .rrm-btn:disabled,
  .rrm-page--light .rrm-btn:disabled,
  .rrm-input:disabled,
  .rrm-page--light .rrm-input:disabled {
    border-color: var(--rrm-border);
    color: var(--rrm-muted);
    background: var(--rrm-surface-soft);
    box-shadow: none;
    opacity: 1;
  }

  .rrm-btn--primary,
  .rrm-page--light .rrm-btn--primary {
    border-color: #9bcfe4;
    color: var(--rrm-brand);
    background: #edf8fc;
  }

  .rrm-page:not(.rrm-page--light) .rrm-btn--primary {
    border-color: var(--rrm-border-strong);
    color: #c8eff9;
    background: #16334d;
  }

  .rrm-btn--green,
  .rrm-page--light .rrm-btn--green {
    border-color: var(--rrm-brand);
    color: #ffffff;
    background: var(--rrm-brand);
  }

  .rrm-page:not(.rrm-page--light) .rrm-btn--green {
    border-color: var(--rrm-horizon);
  }

  .rrm-btn--green:hover:not(:disabled),
  .rrm-page--light .rrm-btn--green:hover:not(:disabled) {
    border-color: var(--rrm-brand-hover);
    color: #ffffff;
    background: var(--rrm-brand-hover);
  }

  .rrm-btn--amber,
  .rrm-page--light .rrm-btn--amber {
    border-color: var(--rrm-danger-border);
    color: var(--rrm-danger);
    background: var(--rrm-danger-bg);
  }

  .rrm-queueBadge,
  .rrm-page--light .rrm-queueBadge {
    border-color: var(--rrm-border);
    color: var(--rrm-muted);
    background: var(--rrm-surface-soft);
    box-shadow: none;
  }

  .rrm-queueBadge.isActive,
  .rrm-page--light .rrm-queueBadge.isActive {
    border-color: var(--rrm-positive-border);
    color: var(--rrm-positive);
    background: var(--rrm-positive-bg);
  }

  .rrm-queueError,
  .rrm-page--light .rrm-queueError,
  .rrm-error,
  .rrm-page--light .rrm-error {
    color: var(--rrm-danger);
  }

  .rrm-tableWrap,
  .rrm-page--light .rrm-tableWrap {
    background: var(--rrm-surface-soft);
    scrollbar-color: var(--rrm-horizon) rgba(34, 58, 122, 0.08);
  }

  .rrm-tableWrap::-webkit-scrollbar-thumb,
  .rrm-page--light .rrm-tableWrap::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: var(--rrm-horizon);
  }

  .rrm-tableWrap::-webkit-scrollbar-track,
  .rrm-page--light .rrm-tableWrap::-webkit-scrollbar-track {
    border-radius: 999px;
    background: rgba(34, 58, 122, 0.08);
  }

  .rrm-table,
  .rrm-page--light .rrm-table {
    color: var(--rrm-text);
    background: var(--rrm-surface);
  }

  .rrm-table thead,
  .rrm-page--light .rrm-table thead {
    background: transparent !important;
    background-color: transparent !important;
  }

  .rrm-table thead th,
  .rrm-page--light .rrm-table thead th,
  .rrm-table thead .rrm-sticky,
  .rrm-page--light .rrm-table thead .rrm-sticky {
    border-bottom: 3px solid var(--rrm-horizon) !important;
    color: #ffffff !important;
    background: var(--rrm-brand) !important;
    background-color: var(--rrm-brand) !important;
    box-shadow: none;
  }

  .rrm-thBtn,
  .rrm-page--light .rrm-thBtn,
  .rrm-thLabel,
  .rrm-page--light .rrm-thLabel {
    color: #ffffff;
  }

  .rrm-thSub,
  .rrm-page--light .rrm-thSub {
    color: #dce8fa;
  }

  .rrm-thBtn:hover,
  .rrm-page--light .rrm-thBtn:hover,
  .rrm-thBtn--filtered,
  .rrm-page--light .rrm-thBtn--filtered {
    background: rgba(61, 182, 233, 0.14);
  }

  .rrm-thBtn--open,
  .rrm-page--light .rrm-thBtn--open {
    background: rgba(61, 182, 233, 0.22);
  }

  .rrm-thBtn:focus-visible,
  .rrm-page--light .rrm-thBtn:focus-visible {
    outline-color: #ffffff;
  }

  .rrm-thDot,
  .rrm-page--light .rrm-thDot {
    background: transparent;
    box-shadow: none;
  }

  .rrm-thDot--on,
  .rrm-page--light .rrm-thDot--on {
    background: var(--rrm-horizon);
    box-shadow: 0 0 0 3px rgba(61, 182, 233, 0.2);
  }

  .rrm-table tbody tr,
  .rrm-page--light .rrm-table tbody tr {
    background: var(--rrm-surface);
  }

  .rrm-table tbody tr:nth-child(even),
  .rrm-page--light .rrm-table tbody tr:nth-child(even) {
    background: var(--rrm-row-even);
  }

  .rrm-table tbody tr:hover,
  .rrm-page--light .rrm-table tbody tr:hover {
    background: var(--rrm-row-hover);
  }

  .rrm-table tbody td,
  .rrm-page--light .rrm-table tbody td {
    border-bottom-color: var(--rrm-border);
    color: var(--rrm-text);
    box-shadow: none;
  }

  .rrm-sticky,
  .rrm-page--light .rrm-sticky {
    background: var(--rrm-surface);
  }

  .rrm-table tbody tr:nth-child(even) .rrm-sticky,
  .rrm-page--light .rrm-table tbody tr:nth-child(even) .rrm-sticky {
    background: var(--rrm-row-even);
  }

  .rrm-table tbody tr:hover .rrm-sticky,
  .rrm-page--light .rrm-table tbody tr:hover .rrm-sticky {
    background: var(--rrm-row-hover);
  }

  .rrm-stickyShadow,
  .rrm-page--light .rrm-stickyShadow {
    box-shadow: inset -1px 0 0 var(--rrm-border);
  }

  .rrm-metaText,
  .rrm-page--light .rrm-metaText,
  .rrm-priceValue,
  .rrm-page--light .rrm-priceValue {
    color: var(--rrm-text);
  }

  .rrm-rateLink,
  .rrm-page--light .rrm-rateLink {
    color: var(--rrm-horizon);
  }

  .rrm-page--light .rrm-rateLink {
    color: var(--rrm-brand);
  }

  .rrm-rateLink:hover,
  .rrm-page--light .rrm-rateLink:hover {
    color: var(--rrm-horizon);
  }

  .rrm-dayHeaderMain,
  .rrm-page--light .rrm-dayHeaderMain {
    color: #ffffff;
  }

  .rrm-dayHeaderSub,
  .rrm-page--light .rrm-dayHeaderSub {
    color: #dce8fa;
  }

  .rrm-priceCellInner,
  .rrm-page--light .rrm-priceCellInner {
    border-color: var(--rrm-border);
    background: var(--rrm-surface-raised);
    box-shadow: none;
  }

  .rrm-priceCellInner:hover,
  .rrm-page--light .rrm-priceCellInner:hover {
    border-color: var(--rrm-horizon);
    background: var(--rrm-surface-raised);
    box-shadow: none;
  }

  .rrm-priceCellInner.stopSell,
  .rrm-page--light .rrm-priceCellInner.stopSell {
    border-color: var(--rrm-danger-border);
    background: var(--rrm-danger-bg);
  }

  .rrm-priceCellInner.automaticStopSell,
  .rrm-page--light .rrm-priceCellInner.automaticStopSell {
    border-color: var(--rrm-positive-border);
    background: var(--rrm-positive-bg);
  }

  .rrm-priceCellInner.pending,
  .rrm-page--light .rrm-priceCellInner.pending,
  .rrm-priceCellInner.dragSelected,
  .rrm-page--light .rrm-priceCellInner.dragSelected {
    border-color: var(--rrm-horizon);
    background: rgba(61, 182, 233, 0.14);
    box-shadow: none;
  }

  .rrm-constraintRow,
  .rrm-page--light .rrm-constraintRow {
    color: var(--rrm-muted);
  }

  .rrm-constraintBadge,
  .rrm-page--light .rrm-constraintBadge {
    color: var(--rrm-muted);
    background: var(--rrm-surface-soft);
  }

  .rrm-stopSellToggle,
  .rrm-page--light .rrm-stopSellToggle,
  .rrm-stopSellToggle.isActive,
  .rrm-page--light .rrm-stopSellToggle.isActive {
    border-color: var(--rrm-danger-border);
    color: #ffffff;
    background: #9b3445;
    box-shadow: none;
    backdrop-filter: none;
  }

  .rrm-stopSellToggle:hover:not(:disabled),
  .rrm-page--light .rrm-stopSellToggle:hover:not(:disabled) {
    border-color: var(--rrm-danger-border);
    color: #ffffff;
    background: #842c3b;
    box-shadow: none;
  }

  .rrm-stopSellToggle.isAutomatic,
  .rrm-page--light .rrm-stopSellToggle.isAutomatic {
    border-color: var(--rrm-positive-border);
    color: var(--rrm-brand);
    background: var(--rrm-connect);
  }

  .rrm-priceInput,
  .rrm-page--light .rrm-priceInput {
    border-color: var(--rrm-horizon);
    color: var(--rrm-text);
    background: var(--rrm-surface-raised);
  }

  .rrm-filterPopover,
  .rrm-page--light .rrm-filterPopover,
  .rrm-cellModal,
  .rrm-page--light .rrm-cellModal {
    border-color: var(--rrm-border);
    border-top-color: var(--rrm-horizon);
    color: var(--rrm-text);
    background: var(--rrm-surface);
    box-shadow: 0 14px 34px rgba(2, 8, 28, 0.26);
  }

  .rrm-page--light .rrm-filterPopover,
  .rrm-page--light .rrm-cellModal {
    box-shadow: 0 12px 28px rgba(34, 58, 122, 0.14);
  }

  .rrm-filterHeader,
  .rrm-page--light .rrm-filterHeader {
    border-bottom-color: var(--rrm-border);
    background: var(--rrm-surface-soft);
  }

  .rrm-filterTitle,
  .rrm-page--light .rrm-filterTitle,
  .rrm-filterOptionLabel,
  .rrm-page--light .rrm-filterOptionLabel {
    color: var(--rrm-ink);
  }

  .rrm-filterSmallBtn,
  .rrm-filterCloseBtn,
  .rrm-page--light .rrm-filterSmallBtn,
  .rrm-page--light .rrm-filterCloseBtn {
    border-color: var(--rrm-border-strong);
    color: var(--rrm-text);
    background: var(--rrm-surface-raised);
  }

  .rrm-filterSmallBtn:hover,
  .rrm-filterCloseBtn:hover,
  .rrm-page--light .rrm-filterSmallBtn:hover,
  .rrm-page--light .rrm-filterCloseBtn:hover,
  .rrm-filterOption:hover,
  .rrm-page--light .rrm-filterOption:hover {
    border-color: var(--rrm-horizon);
    background: var(--rrm-row-hover);
  }

  .rrm-filterInput::placeholder,
  .rrm-page--light .rrm-filterInput::placeholder {
    color: var(--rrm-muted);
    opacity: 1;
  }

  .rrm-filterHint,
  .rrm-page--light .rrm-filterHint,
  .rrm-filterOptionMeta,
  .rrm-page--light .rrm-filterOptionMeta,
  .rrm-cellModal-label,
  .rrm-page--light .rrm-cellModal-label {
    color: var(--rrm-muted);
  }

  .rrm-filterOption,
  .rrm-page--light .rrm-filterOption {
    border-color: var(--rrm-border);
    background: var(--rrm-surface-raised);
  }

  .rrm-filterOption input,
  .rrm-page--light .rrm-filterOption input {
    accent-color: var(--rrm-brand);
  }

  .rrm-filterCountPill,
  .rrm-page--light .rrm-filterCountPill {
    border-color: var(--rrm-border);
    color: var(--rrm-muted);
    background: var(--rrm-surface-soft);
  }

  .rrm-cellModal-count,
  .rrm-page--light .rrm-cellModal-count {
    border-bottom-color: var(--rrm-border);
    color: var(--rrm-brand);
  }

  .rrm-page:not(.rrm-page--light) .rrm-cellModal-count {
    color: var(--rrm-horizon);
  }

  .rrm-cellModal-input:disabled,
  .rrm-page--light .rrm-cellModal-input:disabled {
    border-color: var(--rrm-border);
    color: var(--rrm-muted);
    background: var(--rrm-surface-soft);
    opacity: 1 !important;
  }

  .rrm-cellModal-btn {
    opacity: 1;
  }

  .rrm-cellModal-btn--confirm,
  .rrm-page--light .rrm-cellModal-btn--confirm {
    color: var(--rrm-positive);
    background: var(--rrm-positive-bg);
    box-shadow: 0 0 0 1px var(--rrm-positive-border);
  }

  .rrm-cellModal-btn--cancel,
  .rrm-page--light .rrm-cellModal-btn--cancel {
    color: var(--rrm-danger);
    background: var(--rrm-danger-bg);
    box-shadow: 0 0 0 1px var(--rrm-danger-border);
  }

  .rrm-stopSellNoticeOverlay,
  .rrm-page--light .rrm-stopSellNoticeOverlay {
    background: rgba(23, 38, 83, 0.44);
    backdrop-filter: none;
  }

  .rrm-stopSellNotice,
  .rrm-page--light .rrm-stopSellNotice {
    border-color: var(--rrm-danger-border);
    color: var(--rrm-text);
    background: var(--rrm-surface);
    box-shadow: 0 18px 44px rgba(2, 8, 28, 0.34);
  }

  .rrm-stopSellNoticeIntro,
  .rrm-stopSellNoticeRate,
  .rrm-page--light .rrm-stopSellNoticeIntro,
  .rrm-page--light .rrm-stopSellNoticeRate,
  .rrm-stateSubtitle,
  .rrm-page--light .rrm-stateSubtitle {
    color: var(--rrm-muted);
  }

  .rrm-stopSellNoticeItem,
  .rrm-page--light .rrm-stopSellNoticeItem {
    border-color: var(--rrm-danger-border);
    background: var(--rrm-danger-bg);
  }

  .rrm-stopSellNoticeAction,
  .rrm-page--light .rrm-stopSellNoticeAction {
    color: var(--rrm-danger);
  }

  .rrm-stopSellNoticeButton,
  .rrm-page--light .rrm-stopSellNoticeButton {
    border-color: var(--rrm-brand);
    color: #ffffff;
    background: var(--rrm-brand);
    box-shadow: none;
  }

  .rrm-state,
  .rrm-page--light .rrm-state {
    color: var(--rrm-text);
  }

  @media (max-height: 820px) and (min-width: 769px) {
    .rrm-cardHeader {
      padding: 12px 14px 10px;
      gap: 10px;
    }

    .rrm-title {
      font-size: 1.18rem;
    }

    .rrm-subtitle {
      margin-top: 2px;
      font-size: 0.75rem;
    }

    .rrm-tableWrap {
      --rrm-col-1: 165px;
      --rrm-col-2: 175px;
      --rrm-col-3: 120px;
      --rrm-col-4: 145px;
      --rrm-day-col: 78px;
      padding: 0 7px 8px;
    }

    .rrm-thBtn {
      min-height: 43px;
      padding-block: 6px;
    }

    .rrm-table tbody tr,
    .rrm-table tbody td {
      height: 58px;
      min-height: 58px;
      max-height: 58px;
    }

    .rrm-priceCellInner {
      height: 48px;
      padding: 3px;
    }

    .rrm-metaText,
    .rrm-rateLink {
      max-height: 46px;
      font-size: 0.75rem;
    }
  }

  @media (max-height: 720px) and (min-width: 769px) {
    .rrm-cardHeader {
      padding-block: 9px 8px;
    }

    .rrm-table tbody tr,
    .rrm-table tbody td {
      height: 52px;
      min-height: 52px;
      max-height: 52px;
    }

    .rrm-priceCellInner {
      height: 42px;
    }

    .rrm-metaText,
    .rrm-rateLink {
      max-height: 40px;
    }
  }
`;

const roomRatesMatrixCss = `${uiCss}\n${pilcoreBrandCss}`;

const EditablePriceCell = React.memo(function EditablePriceCell({
  row,
  date,
  value,
  isStopSell,
  isAutomatic,
  isPending,
  onToggleStopSell,
  copy,
  formatPriceValue,
  onCellPointerDown,
  onCellPointerEnter,
  isDragSelected,
  readOnly = false,
  readOnlyTitle = "",
}) {
  const isMutedRate = Boolean(row.base_rate_id || row.has_formula_link);

  const constraints = row.constraints?.[date] || null;
  const hasMin = constraints?.min_stay != null;
  const hasMax = constraints?.max_stay != null;
  const isDefaultOpenRange = constraints?.min_stay === 1 && constraints?.max_stay === 999;
  const shouldShowConstraints = !isDefaultOpenRange && (hasMin || hasMax);

  const stopSellButtonTitle = isAutomatic
    ? copy.cell.automaticStopSell
    : isStopSell
      ? copy.cell.removeStopSell
      : copy.cell.addStopSell;
  const stopSellButtonLabel = isAutomatic ? "A" : isStopSell ? "O" : "C";

  return (
    <td
      className="rrm-priceCell"
      title={
        readOnly ? readOnlyTitle : isMutedRate
          ? copy.cell.readOnlyRateAlert
          : isStopSell
            ? isAutomatic
              ? copy.cell.automaticStopSell
              : copy.cell.stopSellActive
            : copy.cell.clickToEditPrice
      }
      onPointerEnter={() => { if (!readOnly) onCellPointerEnter(row, date); }}
    >
      <div
        className={`rrm-priceCellInner${isMutedRate ? " muted" : ""}${isStopSell ? " stopSell" : ""}${
          isAutomatic ? " automaticStopSell" : ""
        }${
          isPending ? " pending" : ""
        }${isDragSelected ? " dragSelected" : ""}`}
        onPointerDown={(event) => {
          if (readOnly) return;
          onCellPointerDown(row, date, event.currentTarget);
        }}
      >
        <button
          type="button"
          className={`rrm-stopSellToggle${isStopSell ? " isActive" : ""}${isAutomatic ? " isAutomatic" : ""}`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (readOnly) return;
            onToggleStopSell({
              roomRateId: row.room_rate_id,
              date,
              nextStopSell: !isStopSell,
              roomRateName: row.room_rate,
              isAutomatic,
            });
          }}
          disabled={readOnly || !row?.room_rate_id}
          aria-pressed={isStopSell}
          aria-label={stopSellButtonTitle}
          title={readOnly ? `${stopSellButtonTitle} · ${readOnlyTitle}` : stopSellButtonTitle}
        >
          {stopSellButtonLabel}
        </button>

        <div className="rrm-priceValue">{formatPriceValue(value, row)}</div>
        <div className="rrm-constraintRow">
          {shouldShowConstraints ? (
            <>
              {hasMin ? (
                <span className="rrm-constraintBadge">
                  {copy.cell.minShort} {constraints.min_stay}
                </span>
              ) : null}
              {hasMax ? (
                <span className="rrm-constraintBadge">
                  {copy.cell.maxShort} {constraints.max_stay}
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </td>
  );
});

function CellEditModal({ modal, onCommit, onCancel, copy, isLightTheme }) {
  const [tempPrice, setTempPrice] = useState(String(modal.tempPrice ?? ""));
  const [tempMinStay, setTempMinStay] = useState(String(modal.tempMinStay ?? ""));
  const [tempMaxStay, setTempMaxStay] = useState(String(modal.tempMaxStay ?? ""));
  const modalRef = useRef(null);
  const priceInputRef = useRef(null);
  const commitRef = useRef(null);

  commitRef.current = () => onCommit({ price: tempPrice, minStay: tempMinStay, maxStay: tempMaxStay });

  useEffect(() => {
    priceInputRef.current?.select();
  }, []);

  const pos = useMemo(() => {
    if (!modal.anchorRect) return { top: 100, left: 100 };
    const rect = modal.anchorRect;
    const modalWidth = 148;
    const modalHeight = 150;
    const pad = 8;
    const vw = window.innerWidth || 1200;
    const vh = window.innerHeight || 800;

    let left = rect.left + rect.width / 2 - modalWidth / 2;
    let top = rect.bottom + pad;

    if (top + modalHeight > vh - pad) {
      top = rect.top - modalHeight - pad;
    }

    left = Math.max(pad, Math.min(left, vw - modalWidth - pad));
    top = Math.max(pad, top);

    return { top, left };
  }, [modal.anchorRect]);

  useEffect(() => {
    const onMouseDown = (event) => {
      if (modalRef.current?.contains(event.target)) return;
      commitRef.current();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onCancel();
      }
    };

    const timer = setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 60);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onCancel]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitRef.current();
    }
  };

  const cellCount = modal.cells.length;
  const priceReadOnly = modal.firstIsMuted;
  const firstCurrencyCell = modal.cells[0] || {};
  const priceStep = currencyAmountStep(
    firstCurrencyCell.currency,
    rowCurrencyCatalog(firstCurrencyCell)
  );

  return (
    <div
      ref={modalRef}
      className={`rrm-cellModal${isLightTheme ? " rrm-cellModal--light" : ""}`}
      style={{ top: pos.top, left: pos.left }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      {cellCount > 1 && (
        <div className="rrm-cellModal-count">{copy.cell.modalCellCount(cellCount)}</div>
      )}
      <div className="rrm-cellModal-field">
        <span className="rrm-cellModal-label">{copy.cell.modalPrice}</span>
        <input
          ref={priceInputRef}
          type="number"
          step={priceStep}
          className="rrm-cellModal-input"
          value={priceReadOnly ? "" : tempPrice}
          onChange={(event) => { if (!priceReadOnly) setTempPrice(event.target.value); }}
          onKeyDown={handleKeyDown}
          disabled={priceReadOnly}
          placeholder={priceReadOnly ? "—" : undefined}
          style={priceReadOnly ? { opacity: 0.35, cursor: "not-allowed" } : undefined}
        />
      </div>
      <div className="rrm-cellModal-field">
        <span className="rrm-cellModal-label">{copy.cell.modalMinStay}</span>
        <input
          type="number"
          className="rrm-cellModal-input"
          value={tempMinStay}
          onChange={(event) => setTempMinStay(event.target.value)}
          onKeyDown={handleKeyDown}
          min="1"
          step="1"
        />
      </div>
      <div className="rrm-cellModal-field">
        <span className="rrm-cellModal-label">{copy.cell.modalMaxStay}</span>
        <input
          type="number"
          className="rrm-cellModal-input"
          value={tempMaxStay}
          onChange={(event) => setTempMaxStay(event.target.value)}
          onKeyDown={handleKeyDown}
          min="1"
          step="1"
        />
      </div>
      <div className="rrm-cellModal-actions">
        <button
          type="button"
          className="rrm-cellModal-btn rrm-cellModal-btn--confirm"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => commitRef.current()}
          title="Confirmar"
        >
          ✓
        </button>
        <button
          type="button"
          className="rrm-cellModal-btn rrm-cellModal-btn--cancel"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={onCancel}
          title="Cancelar"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

function StopSellNoticeModal({ notice, copy, onClose }) {
  if (!notice?.items?.length) return null;

  return (
    <div
      className="rrm-stopSellNoticeOverlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rrm-stopSellNoticeTitle"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="rrm-stopSellNotice" onPointerDown={(event) => event.stopPropagation()}>
        <div id="rrm-stopSellNoticeTitle" className="rrm-stopSellNoticeTitle">
          {copy.stopSellNotice.title}
        </div>
        <div className="rrm-stopSellNoticeIntro">{copy.stopSellNotice.intro}</div>
        <div className="rrm-stopSellNoticeList">
          {notice.items.map((item) => (
            <div key={`${item.roomRateId || "rate"}|${item.date}|${item.stopSell}`} className="rrm-stopSellNoticeItem">
              <div className="rrm-stopSellNoticeMain">
                <span>{item.date}</span>
                <span className="rrm-stopSellNoticeAction">
                  {item.stopSell ? copy.stopSellNotice.closeSale : copy.stopSellNotice.openSale}
                </span>
              </div>
              <div className="rrm-stopSellNoticeRate">
                {item.roomRateName || copy.stopSellNotice.unknownRate}
                {item.reason ? ` - ${item.reason}` : ""}
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="rrm-stopSellNoticeButton" onClick={onClose}>
          {copy.stopSellNotice.confirm}
        </button>
      </div>
    </div>
  );
}

export default function RoomRatesMatrix({ onOpenBulkApply = null, onOpenStopSellsOverview = null }) {
  const demoModeActive = isDemoMode();
  const navigate = useNavigate();
  const { themeId, languageId, language } = usePmsTheme();
  const demoCopy = DEMO_MATRIX_MESSAGES[languageId] || DEMO_MATRIX_MESSAGES.en;
  const isLightTheme = themeId === "light";
  const matrixCopy = ROOM_RATES_MATRIX_MESSAGES[languageId] || ROOM_RATES_MATRIX_MESSAGES.en;
  const matrixCopyRef = useRef(matrixCopy);
  const dayLabelLocale = languageId === "en" ? "en-GB" : language?.locale;
  const filterSortLocale = languageId === "en" ? undefined : language?.locale;
  const numberFormatLocale = languageId === "en" ? undefined : language?.locale;
  const [matrixData, setMatrixData] = useState([]);
  const [dates, setDates] = useState([]);
  const [range, setRange] = useState({ start: "", end: "" });
  const [columnFilters, setColumnFilters] = useState(getDefaultMatrixColumnFilters);
  const [activeFilter, setActiveFilter] = useState(null); // { key, rect }
  const [filterOptionQuery, setFilterOptionQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stopSellKeys, setStopSellKeys] = useState(new Set());
  const [automaticStopSellKeys, setAutomaticStopSellKeys] = useState(new Set());

  const [isQueueProcessing, setIsQueueProcessing] = useState(false);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [pendingCellKeys, setPendingCellKeys] = useState(new Set());
  const [queueError, setQueueError] = useState("");
  const [cellModal, setCellModal] = useState(null);
  const [stopSellFailureNotice, setStopSellFailureNotice] = useState(null);
  const [dragKeys, setDragKeys] = useState(new Set());
  const shouldRefreshFromOtherTabs = useCallback(() => !demoModeActive && !isDemoMode(), [demoModeActive]);
  const crossTabRefreshToken = useCrossTabDataRefreshToken(shouldRefreshFromOtherTabs);
  const effectiveRefreshToken = demoModeActive ? 0 : crossTabRefreshToken;

  const visitorTodayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const matrixTodayIso = demoModeActive ? demoReferenceDate() : visitorTodayIso;
  const requestStartMin = matrixTodayIso;
  const requestEndMin = range.start || matrixTodayIso;
  const requestEndMax = useMemo(
    () => addDaysIso(range.start || matrixTodayIso, MAX_MATRIX_RANGE_DAYS - 1),
    [matrixTodayIso, range.start]
  );

  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const pendingByCellRef = useRef(new Map());
  const stopSellBatchRef = useRef(new Map());
  const stopSellBatchTimerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const filterPopoverRef = useRef(null);
  const loadedWindowRef = useRef({ start: "", end: "" });
  const lastDemoRequestRef = useRef(null);
  const fetchMatrixSeqRef = useRef(0);
  const dragRef = useRef({ active: false, cells: [], firstElement: null, firstConstraints: null, firstPrice: null });

  const currentUser = demoModeActive ? readDemoUser() : JSON.parse(localStorage.getItem("palantir_user"));
  const businessUnitId = demoModeActive ? DEMO_BUSINESS_UNIT_ID : currentUser?.active_business_unit_id;

  useEffect(() => {
    matrixCopyRef.current = matrixCopy;
  }, [matrixCopy]);

  const formatPriceValue = useCallback(
    (value, row) => formatPrice(value, numberFormatLocale, row),
    [numberFormatLocale]
  );

  const applyPendingByCellDelta = useCallback((cellKey, delta) => {
    if (demoModeActive || isDemoMode()) return;
    const nextMap = pendingByCellRef.current;
    const nextValue = (nextMap.get(cellKey) || 0) + delta;

    if (nextValue <= 0) {
      nextMap.delete(cellKey);
    } else {
      nextMap.set(cellKey, nextValue);
    }

    setPendingCellKeys(new Set(nextMap.keys()));
  }, [demoModeActive]);

  const fetchMatrix = useCallback(
    async ({ silent = false, startDate = "", endDate = "", selectedRange = null } = {}) => {
      if (!businessUnitId) return;

      if (!silent || demoModeActive) {
        setLoading(true);
        setError("");
      }
      if (demoModeActive) {
        setMatrixData([]);
        setStopSellKeys(new Set());
        setAutomaticStopSellKeys(new Set());
      }

      const requestSeq = fetchMatrixSeqRef.current + 1;
      fetchMatrixSeqRef.current = requestSeq;

      try {
        if (demoModeActive && !matrixTodayIso) throw new Error(demoCopy.update);
        const fallbackStart = loadedWindowRef.current.start || matrixTodayIso;
        const fallbackEnd =
          loadedWindowRef.current.end ||
          addDaysIso(matrixTodayIso, INITIAL_MATRIX_RANGE_DAYS - 1);
        const requestRange = sanitizeRequestRange(
          startDate || fallbackStart,
          endDate || fallbackEnd,
          matrixTodayIso
        );
        const params = new URLSearchParams({
          business_unit_id: String(businessUnitId),
          start_date: requestRange.start,
          end_date: requestRange.end,
        });
        if (demoModeActive) {
          lastDemoRequestRef.current = { startDate: requestRange.start, endDate: requestRange.end, selectedRange };
        }

        // Prices and stop-sell metadata are mandatory together in the demo.
        // Missing data must not appear as an empty/open matrix.
        const [response, demoStops] = demoModeActive
          ? await Promise.all([
              axios.get(`/api/rate-prices/matrix?${params.toString()}`),
              axios.get(`/api/stop-sells?business_unit_id=${businessUnitId}`),
            ])
          : [await axios.get(`/api/rate-prices/matrix?${params.toString()}`), null];

        if (demoModeActive && (
          !Array.isArray(response.data?.matrix) || !Array.isArray(response.data?.dates)
          || !Array.isArray(demoStops?.data)
          || demoStops.data.some((row) => !Object.prototype.hasOwnProperty.call(row, "is_automatic")
            || (row.is_automatic !== null && typeof row.is_automatic !== "boolean"))
        )) throw new Error(demoCopy.update);

        const nextRows = Array.isArray(response.data?.matrix) ? response.data.matrix : [];
        const nextDates = Array.isArray(response.data?.dates) ? response.data.dates : [];

        if (requestSeq !== fetchMatrixSeqRef.current) return;

        if (demoModeActive) {
          const nextStopKeys = new Set();
          const nextAutomaticKeys = new Set();
          for (const row of demoStops.data) {
            const key = `${row.room_rate_id}|${normalizeIsoDate(row.stay_date)}`;
            nextStopKeys.add(key);
            if (isAutomaticStopSell(row)) nextAutomaticKeys.add(key);
          }
          setStopSellKeys(nextStopKeys);
          setAutomaticStopSellKeys(nextAutomaticKeys);
        }

        setMatrixData(sortRows(nextRows));
        setDates(nextDates);
        loadedWindowRef.current = {
          start: nextDates[0] || requestRange.start,
          end: nextDates[nextDates.length - 1] || requestRange.end,
        };
        setRange((previous) => {
          if (selectedRange) {
            return sanitizeRange(selectedRange.start, selectedRange.end, nextDates);
          }

          if (previous.start && previous.end) {
            return sanitizeRange(previous.start, previous.end, nextDates);
          }

          return sanitizeRange(
            nextDates[0],
            nextDates[Math.min(DEFAULT_VISIBLE_DAYS - 1, nextDates.length - 1)],
            nextDates
          );
        });
      } catch (fetchError) {
        if (requestSeq !== fetchMatrixSeqRef.current) return;
        setError(demoModeActive
          ? (fetchError?.response?.data?.error || fetchError?.message || matrixCopyRef.current.messages.loadError)
          : matrixCopyRef.current.messages.loadError);
      } finally {
        if ((!silent || demoModeActive) && requestSeq === fetchMatrixSeqRef.current) setLoading(false);
      }
    },
    [businessUnitId, matrixTodayIso, demoModeActive, demoCopy.update]
  );

  const fetchStopSells = useCallback(async () => {
    if (demoModeActive || isDemoMode()) return;
    if (!businessUnitId) return;

    try {
      const response = await axios.get(`/api/stop-sells?business_unit_id=${businessUnitId}`);
      const nextSet = new Set();
      const nextAutomaticSet = new Set();

      for (const stopSell of response.data || []) {
        const rateId = stopSell?.room_rate_id;
        const stayDate = normalizeIsoDate(stopSell?.stay_date);
        if (rateId && stayDate.length === 10) {
          const key = `${rateId}|${stayDate}`;
          nextSet.add(key);
          if (isAutomaticStopSell(stopSell)) {
            nextAutomaticSet.add(key);
          }
        }
      }

      setStopSellKeys(nextSet);
      setAutomaticStopSellKeys(nextAutomaticSet);
    } catch (fetchError) {
      setStopSellKeys(new Set());
      setAutomaticStopSellKeys(new Set());
    }
  }, [businessUnitId, demoModeActive]);

  const handleRequestedRangeChange = useCallback(
    (nextStart, nextEnd) => {
      const requestedRange = sanitizeRequestRange(nextStart, nextEnd, matrixTodayIso);
      setRange(requestedRange);

      if (!rangeIsLoaded(requestedRange, dates)) {
        fetchMatrix({
          startDate: requestedRange.start,
          endDate: requestedRange.end,
          selectedRange: requestedRange,
        });
      }
    },
    [dates, fetchMatrix, matrixTodayIso]
  );

  const scheduleBackgroundSync = useCallback(() => {
    if (demoModeActive || isDemoMode()) return;
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
    }

    syncTimerRef.current = setTimeout(() => {
      if (isDemoMode()) return;
      fetchMatrix({ silent: true });
      fetchStopSells();
      syncTimerRef.current = null;
    }, 1200);
  }, [fetchMatrix, fetchStopSells, demoModeActive]);

  const showStopSellFailureNotice = useCallback((items) => {
    if (demoModeActive || isDemoMode()) return;
    const deduped = new Map();

    for (const item of items) {
      if (!item?.date) continue;
      deduped.set(`${item.roomRateId || ""}|${item.date}|${item.stopSell ? 1 : 0}`, item);
    }

    const nextItems = Array.from(deduped.values());
    if (nextItems.length > 0) {
      setStopSellFailureNotice({ items: nextItems });
    }
  }, [demoModeActive]);

  const overlayPendingStopSellBatch = useCallback(() => {
    if (demoModeActive || isDemoMode()) return;
    if (stopSellBatchRef.current.size === 0) return;

    setStopSellKeys((previous) => {
      const nextKeys = new Set(previous);
      stopSellBatchRef.current.forEach((item) => {
        const key = `${item.room_rate_id}|${item.stay_date}`;
        if (item.stop_sell) {
          nextKeys.add(key);
        } else {
          nextKeys.delete(key);
        }
      });
      return nextKeys;
    });
    setAutomaticStopSellKeys((previous) => {
      const nextKeys = new Set(previous);
      stopSellBatchRef.current.forEach((item) => {
        const key = `${item.room_rate_id}|${item.stay_date}`;
        nextKeys.delete(key);
      });
      return nextKeys;
    });
  }, [demoModeActive]);

  const flushStopSellBatch = useCallback(async () => {
    if (demoModeActive || isDemoMode()) return;
    const batch = Array.from(stopSellBatchRef.current.values());
    stopSellBatchRef.current.clear();
    stopSellBatchTimerRef.current = null;

    if (!batch.length || !businessUnitId) return;

    const batchByCell = new Map(batch.map((item) => [`${item.room_rate_id}|${item.stay_date}`, item]));

    try {
      const response = await axios.post("/api/stop-sells/apply-matrix", {
        business_unit_id: businessUnitId,
        changes: batch,
      });

      const failed = Array.isArray(response.data?.failed) ? response.data.failed : [];

      if (failed.length > 0) {
        await fetchStopSells();
        overlayPendingStopSellBatch();
        showStopSellFailureNotice(
          failed.map((item) => {
            const fallback = batchByCell.get(`${item.room_rate_id}|${item.stay_date}`);
            return {
              roomRateId: item.room_rate_id,
              date: item.stay_date,
              stopSell: Boolean(item.stop_sell),
              roomRateName: item.rate_name || fallback?.room_rate_name || "",
              reason: item.reason || "",
            };
          })
        );
        return;
      }

      scheduleBackgroundSync();
    } catch (saveError) {
      await fetchStopSells();
      overlayPendingStopSellBatch();
      showStopSellFailureNotice(
        batch.map((item) => ({
          roomRateId: item.room_rate_id,
          date: item.stay_date,
          stopSell: Boolean(item.stop_sell),
          roomRateName: item.room_rate_name || "",
          reason: "",
        }))
      );
    }
  }, [businessUnitId, fetchStopSells, overlayPendingStopSellBatch, scheduleBackgroundSync, showStopSellFailureNotice, demoModeActive]);

  const toggleStopSell = useCallback(
    ({ roomRateId, date, nextStopSell, roomRateName, isAutomatic }) => {
      if (demoModeActive || isDemoMode()) return;
      if (!roomRateId || !date) return;

      const cellKey = `${roomRateId}|${date}`;
      setQueueError("");

      if (isAutomatic && !nextStopSell) {
        setQueueError(matrixCopyRef.current.cell.automaticStopSell);
        return;
      }

      setStopSellKeys((previous) => {
        const nextKeys = new Set(previous);
        if (nextStopSell) {
          nextKeys.add(cellKey);
        } else {
          nextKeys.delete(cellKey);
        }
        return nextKeys;
      });
      setAutomaticStopSellKeys((previous) => {
        const nextKeys = new Set(previous);
        nextKeys.delete(cellKey);
        return nextKeys;
      });

      stopSellBatchRef.current.set(cellKey, {
        room_rate_id: roomRateId,
        stay_date: date,
        stop_sell: Boolean(nextStopSell),
        room_rate_name: roomRateName || "",
      });

      if (stopSellBatchTimerRef.current) {
        clearTimeout(stopSellBatchTimerRef.current);
      }

      stopSellBatchTimerRef.current = setTimeout(() => {
        flushStopSellBatch();
      }, STOP_SELL_BATCH_WINDOW_MS);
    },
    [flushStopSellBatch, demoModeActive]
  );

  const processQueue = useCallback(async () => {
    if (demoModeActive || isDemoMode()) return;
    if (processingRef.current) return;
    processingRef.current = true;
    setIsQueueProcessing(true);

    while (queueRef.current.length > 0) {
      if (isDemoMode()) break;
      const task = queueRef.current.shift();

      try {
        await axios.post("/api/rate-prices", {
          room_rate_id: task.roomRateId,
          start_date: task.date,
          end_date: task.date,
          price: task.price,
        });
        if (task.minStay !== undefined && !isDemoMode()) {
          await axios.patch("/api/rate-prices/set-min-stay", {
            room_rate_id: task.roomRateId,
            start_date: task.date,
            end_date: task.date,
            min_stay: task.minStay,
          });
        }
        if (task.maxStay !== undefined && !isDemoMode()) {
          await axios.patch("/api/rate-prices/set-max-stay", {
            room_rate_id: task.roomRateId,
            start_date: task.date,
            end_date: task.date,
            max_stay: task.maxStay,
          });
        }
      } catch (saveError) {
        const apiMessage = saveError?.response?.data?.error;
        const fallbackMessage = saveError?.message || matrixCopyRef.current.messages.unknownError;
        setQueueError(matrixCopyRef.current.queue.priceSaveError(apiMessage || fallbackMessage));
      } finally {
        setPendingTasksCount((prev) => Math.max(0, prev - 1));
        applyPendingByCellDelta(task.cellKey, -1);
      }
    }

    processingRef.current = false;
    setIsQueueProcessing(false);

    if (queueRef.current.length > 0) {
      processQueue();
    } else {
      scheduleBackgroundSync();
    }
  }, [applyPendingByCellDelta, scheduleBackgroundSync, demoModeActive]);

  const enqueuePriceUpdate = useCallback(
    ({ roomRateId, date, price, rowKey, minStay = undefined, maxStay = undefined }) => {
      if (demoModeActive || isDemoMode()) return;
      const cellKey = `${roomRateId}|${date}`;

      let removedDuplicates = 0;
      queueRef.current = queueRef.current.filter((queuedTask) => {
        const sameCell = queuedTask.cellKey === cellKey;
        if (sameCell) removedDuplicates += 1;
        return !sameCell;
      });

      if (removedDuplicates > 0) {
        setPendingTasksCount((prev) => Math.max(0, prev - removedDuplicates));
        applyPendingByCellDelta(cellKey, -removedDuplicates);
      }

      queueRef.current.push({
        roomRateId,
        date,
        price,
        rowKey,
        cellKey,
        minStay,
        maxStay,
      });

      setPendingTasksCount((prev) => prev + 1);
      applyPendingByCellDelta(cellKey, 1);
      setQueueError("");

      processQueue();
    },
    [applyPendingByCellDelta, processQueue, demoModeActive]
  );

  const handleCellPointerDown = useCallback((row, date, element) => {
    if (demoModeActive || isDemoMode()) return;
    const constraints = row.constraints?.[date] ?? {};
    const key = `${row.room_rate_id}|${date}`;

    const isMuted = Boolean(row.base_rate_id || row.has_formula_link);
    dragRef.current = {
      active: true,
      cells: [{ rowKey: rowKeyFor(row), roomRateId: row.room_rate_id, date, minPriceAmount: row.min_price_amount ?? null, maxPriceAmount: row.max_price_amount ?? null, currency: row.currency, minor_units: row.minor_units, isMuted }],
      firstElement: element,
      firstConstraints: constraints,
      firstPrice: row.prices?.[date] ?? "",
      firstIsMuted: isMuted,
    };

    setDragKeys(new Set([key]));
    window.getSelection()?.removeAllRanges();
  }, [demoModeActive]);

  const handleCellPointerEnter = useCallback((row, date) => {
    if (demoModeActive || isDemoMode()) return;
    if (!dragRef.current.active) return;

    const key = `${row.room_rate_id}|${date}`;
    const existingKeys = new Set(dragRef.current.cells.map((c) => `${c.roomRateId}|${c.date}`));
    const firstCurrency = String(dragRef.current.cells[0]?.currency || "").toUpperCase();
    const nextCurrency = String(row?.currency || "").toUpperCase();
    if (firstCurrency !== nextCurrency) return;

    if (!existingKeys.has(key)) {
      dragRef.current.cells.push({ rowKey: rowKeyFor(row), roomRateId: row.room_rate_id, date, minPriceAmount: row.min_price_amount ?? null, maxPriceAmount: row.max_price_amount ?? null, currency: row.currency, minor_units: row.minor_units, isMuted: Boolean(row.base_rate_id || row.has_formula_link) });
      setDragKeys(new Set([...existingKeys, key]));
    }
  }, [demoModeActive]);

  const commitCellModal = useCallback(
    async ({ price, minStay, maxStay }) => {
      if (demoModeActive || isDemoMode()) return;
      if (!cellModal) return;
      const { cells } = cellModal;

      if (!matrixPriceHasValidPrecision(price, cells)) {
        const firstCell = cells[0] || {};
        const currency = String(firstCell.currency || "").toUpperCase();
        const minorUnits = getCurrencyMinorUnits(currency, rowCurrencyCatalog(firstCell));
        setQueueError(matrixCopyRef.current.cell.invalidCurrencyPrecision(currency, minorUnits));
        return;
      }

      setCellModal(null);
      if (!dragRef.current.active) {
        setDragKeys(new Set());
      }

      const parsedPrice = price === "" ? null : Number(price);
      const parsedMin = minStay === "" ? null : Number(minStay);
      const parsedMax = maxStay === "" ? null : Number(maxStay);

      // --- Min/max stay: distinguish "skip" from "explicit clear" ---
      // undefined = no change (field was already empty); null = clear (user deleted existing value); number = set
      const originalMin = cellModal.tempMinStay;
      const originalMax = cellModal.tempMaxStay;
      const hadOriginalMin = originalMin !== null && originalMin !== undefined && originalMin !== "";
      const hadOriginalMax = originalMax !== null && originalMax !== undefined && originalMax !== "";
      const newMinEmpty = minStay === "" || minStay === null || minStay === undefined;
      const newMaxEmpty = maxStay === "" || maxStay === null || maxStay === undefined;

      // effectiveMin/Max: undefined = skip API call, null = send null (clear), number = send number
      const effectiveMin = newMinEmpty
        ? (hadOriginalMin ? null : undefined)
        : (Number.isFinite(parsedMin) && parsedMin >= 1 ? parsedMin : undefined);
      const effectiveMax = newMaxEmpty
        ? (hadOriginalMax ? null : undefined)
        : (Number.isFinite(parsedMax) && parsedMax >= 1 ? parsedMax : undefined);

      for (const cell of cells) {
        if (isDemoMode()) break;
        if (!cell.roomRateId) continue;

        // --- Price: clamp to rate limits if defined ---
        let validPrice = parsedPrice !== null && Number.isFinite(parsedPrice) ? parsedPrice : null;
        if (validPrice !== null) {
          if (cell.minPriceAmount !== null && validPrice < cell.minPriceAmount) validPrice = cell.minPriceAmount;
          if (cell.maxPriceAmount !== null && validPrice > cell.maxPriceAmount) validPrice = cell.maxPriceAmount;
        }

        // Optimistic local update
        setMatrixData((previous) =>
          previous.map((currentRow) => {
            if (rowKeyFor(currentRow) !== cell.rowKey) return currentRow;
            const nextPrices = validPrice !== null
              ? { ...currentRow.prices, [cell.date]: validPrice }
              : currentRow.prices;
            const hasConstraintChange = effectiveMin !== undefined || effectiveMax !== undefined;
            const nextConstraints = hasConstraintChange
              ? {
                  ...currentRow.constraints,
                  [cell.date]: {
                    ...(currentRow.constraints?.[cell.date] || {}),
                    ...(effectiveMin !== undefined ? { min_stay: effectiveMin } : {}),
                    ...(effectiveMax !== undefined ? { max_stay: effectiveMax } : {}),
                  },
                }
              : currentRow.constraints;
            return { ...currentRow, prices: nextPrices, constraints: nextConstraints };
          })
        );

        if (validPrice !== null && !cell.isMuted) {
          // Price + constraints go through the queue (price save clears constraints, so they must follow immediately)
          enqueuePriceUpdate({
            roomRateId: cell.roomRateId,
            date: cell.date,
            price: validPrice,
            rowKey: cell.rowKey,
            minStay: effectiveMin,
            maxStay: effectiveMax,
          });
        } else if (effectiveMin !== undefined || effectiveMax !== undefined) {
          // Constraints-only: save directly without queuing price
          if (effectiveMin !== undefined) {
            axios.patch("/api/rate-prices/set-min-stay", {
              room_rate_id: cell.roomRateId,
              start_date: cell.date,
              end_date: cell.date,
              min_stay: effectiveMin,
            }).finally(() => scheduleBackgroundSync());
          }
          if (effectiveMax !== undefined) {
            axios.patch("/api/rate-prices/set-max-stay", {
              room_rate_id: cell.roomRateId,
              start_date: cell.date,
              end_date: cell.date,
              max_stay: effectiveMax,
            }).finally(() => scheduleBackgroundSync());
          }
        }
      }
    },
    [cellModal, enqueuePriceUpdate, scheduleBackgroundSync, demoModeActive]
  );

  const cancelCellModal = useCallback(() => {
    setCellModal(null);
    setDragKeys(new Set());
  }, []);

  useEffect(() => {
    if (demoModeActive) {
      queueRef.current = [];
      stopSellBatchRef.current.clear();
      pendingByCellRef.current.clear();
      clearTimeout(stopSellBatchTimerRef.current);
      clearTimeout(syncTimerRef.current);
      setPendingCellKeys(new Set());
      setPendingTasksCount(0);
      setIsQueueProcessing(false);
      setCellModal(null);
      setStopSellFailureNotice(null);
      setDragKeys(new Set());
      dragRef.current.active = false;
    }
    if (!businessUnitId) {
      setLoading(false);
      setError(matrixCopyRef.current.messages.noBusinessUnit);
      return;
    }

    fetchMatrix();
    if (!demoModeActive) fetchStopSells();
  }, [businessUnitId, fetchMatrix, fetchStopSells, effectiveRefreshToken, demoModeActive]);

  useEffect(() => {
    if (dates.length === 0) {
      setRange({ start: "", end: "" });
      return;
    }

    setRange((previous) => {
      const fallbackEnd = dates[Math.min(DEFAULT_VISIBLE_DAYS - 1, dates.length - 1)];
      return sanitizeRange(previous.start || dates[0], previous.end || fallbackEnd, dates);
    });
  }, [dates]);

  useEffect(
    () => () => {
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (demoModeActive) return undefined;
    const onPointerUp = () => {
      if (isDemoMode()) return;
      if (!dragRef.current.active) return;
      const { cells, firstElement, firstConstraints, firstPrice, firstIsMuted } = dragRef.current;
      dragRef.current = { active: false, cells: [], firstElement: null, firstConstraints: null, firstPrice: null, firstIsMuted: false };
      if (!cells.length) return;
      const rect = firstElement?.getBoundingClientRect() ?? null;
      setCellModal({
        cells,
        anchorRect: rect,
        tempPrice: firstPrice ?? "",
        tempMinStay: firstConstraints?.min_stay ?? "",
        tempMaxStay: firstConstraints?.max_stay ?? "",
        firstIsMuted: Boolean(firstIsMuted),
      });
    };
    window.addEventListener("pointerup", onPointerUp);
    return () => window.removeEventListener("pointerup", onPointerUp);
  }, [demoModeActive]);

  const closeFilterPopover = useCallback(() => {
    setActiveFilter(null);
  }, []);

  const toggleFilterPopover = useCallback((key, triggerElement) => {
    setActiveFilter((previous) => {
      if (previous?.key === key) return null;
      return { key, rect: triggerElement.getBoundingClientRect() };
    });
  }, []);

  const toggleMultiSelectFilter = useCallback((key, value) => {
    setColumnFilters((previous) => {
      const previousValues = Array.isArray(previous[key]) ? previous[key] : [];
      const exists = previousValues.includes(value);
      const nextValues = exists ? previousValues.filter((item) => item !== value) : [...previousValues, value];
      return { ...previous, [key]: nextValues };
    });
  }, []);

  const clearColumnFilter = useCallback((key) => {
    setColumnFilters((previous) => ({
      ...previous,
      [key]: [],
    }));
  }, []);

  useEffect(() => {
    setFilterOptionQuery("");
  }, [activeFilter?.key]);

  useEffect(() => {
    if (!activeFilter?.key) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeFilterPopover();
    };

    const onMouseDown = (event) => {
      if (filterPopoverRef.current?.contains(event.target)) return;
      if (event.target?.closest?.("[data-rrm-filter-trigger]")) return;
      closeFilterPopover();
    };

    const onResize = () => closeFilterPopover();

    const onScroll = (event) => {
      if (filterPopoverRef.current?.contains(event.target)) return;
      closeFilterPopover();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [activeFilter?.key, closeFilterPopover]);

  const visibleDates = useMemo(() => {
    if (!range.start || !range.end || dates.length === 0) return [];
    return dates.filter((date) => date >= range.start && date <= range.end);
  }, [dates, range.end, range.start]);

  const roomRateNameById = useMemo(() => {
    const map = new Map();
    for (const row of matrixData) {
      if (row?.room_rate_id && row?.room_rate) {
        map.set(row.room_rate_id, row.room_rate);
      }
    }
    return map;
  }, [matrixData]);

  const getRowFilterLabel = useCallback(
    (row, key) => {
      if (key === "roomCategory") return normalizeFilterLabel(row?.room_category);
      if (key === "roomRate") return normalizeFilterLabel(row?.room_rate);
      if (key === "client") return normalizeFilterLabel(row?.client);
      if (key === "dependentFrom") {
        if (!row?.base_rate_id) return "-";
        return normalizeFilterLabel(roomRateNameById.get(row.base_rate_id) || "-");
      }
      return "-";
    },
    [roomRateNameById]
  );

  const setFilterStats = useMemo(() => {
    const buildStringStats = (key) => {
      const counts = new Map();
      const labels = new Map();

      for (const row of matrixData) {
        const label = getRowFilterLabel(row, key);
        const value = normalizeFilterValue(label);
        counts.set(value, (counts.get(value) || 0) + 1);
        if (!labels.has(value)) labels.set(value, label);
      }

      const options = Array.from(labels.entries())
        .map(([value, label]) => ({
          value,
          label,
          count: counts.get(value) || 0,
        }))
        .sort((left, right) =>
          left.label.localeCompare(right.label, filterSortLocale, { sensitivity: "base" })
        );

      return { options, labels };
    };

    return {
      roomCategory: buildStringStats("roomCategory"),
      roomRate: buildStringStats("roomRate"),
      client: buildStringStats("client"),
      dependentFrom: buildStringStats("dependentFrom"),
    };
  }, [filterSortLocale, getRowFilterLabel, matrixData]);

  const columnIsFiltered = useCallback((key) => {
    const value = columnFilters[key];
    return Array.isArray(value) ? value.length > 0 : String(value || "").trim().length > 0;
  }, [columnFilters]);

  const columnSubtitle = useCallback(
    (key) => {
      const value = columnFilters[key];
      if (!Array.isArray(value) || value.length === 0) return matrixCopy.filters.all;

      if (value.length === 1) {
        const only = value[0];
        const labels =
          key === "roomCategory"
            ? setFilterStats.roomCategory.labels
            : key === "roomRate"
              ? setFilterStats.roomRate.labels
              : key === "client"
                ? setFilterStats.client.labels
                : key === "dependentFrom"
                  ? setFilterStats.dependentFrom.labels
                  : null;

        return labels?.get(only) || String(only);
      }

      if (key === "roomCategory") return matrixCopy.filters.categoriesCount(value.length);
      if (key === "roomRate") return matrixCopy.filters.ratesCount(value.length);
      if (key === "client") return matrixCopy.filters.clientsCount(value.length);
      if (key === "dependentFrom") return matrixCopy.filters.ratesCount(value.length);
      return matrixCopy.filters.selectedCount(value.length);
    },
    [columnFilters, matrixCopy.filters, setFilterStats]
  );

  const filterModel = useMemo(() => {
    const key = activeFilter?.key;
    if (!key) return null;
    if (key === "roomCategory")
      return { key, title: matrixCopy.filters.roomCategory, type: "set", options: setFilterStats.roomCategory.options };
    if (key === "roomRate")
      return { key, title: matrixCopy.filters.roomRate, type: "set", options: setFilterStats.roomRate.options };
    if (key === "client")
      return { key, title: matrixCopy.filters.client, type: "set", options: setFilterStats.client.options };
    if (key === "dependentFrom")
      return {
        key,
        title: matrixCopy.filters.dependentFrom,
        type: "set",
        options: setFilterStats.dependentFrom.options,
      };
    return null;
  }, [activeFilter?.key, matrixCopy.filters, setFilterStats]);

  const filterPopoverPos = useMemo(() => {
    if (!activeFilter?.rect) return null;

    const rect = activeFilter.rect;
    const pad = 12;
    const viewportWidth = window.innerWidth || 1200;
    const viewportHeight = window.innerHeight || 800;

    const width = Math.min(380, viewportWidth - pad * 2);
    const estimatedHeight = 420;

    let left = rect.left;
    left = Math.max(pad, Math.min(left, viewportWidth - width - pad));

    const showBelow = rect.bottom + 10 + estimatedHeight <= viewportHeight - pad;
    let top = showBelow ? rect.bottom + 10 : rect.top - estimatedHeight - 10;
    top = Math.max(pad, Math.min(top, viewportHeight - estimatedHeight - pad));

    return { top, left, width };
  }, [activeFilter?.rect]);

  const visibleFilterOptions = useMemo(() => {
    if (!filterModel || filterModel.type !== "set") return [];
    const needle = filterOptionQuery.trim().toLowerCase();
    if (!needle) return filterModel.options;

    return filterModel.options.filter((option) => {
      const label = String(option.label || "").toLowerCase();
      const meta = String(option.meta || "").toLowerCase();
      return label.includes(needle) || meta.includes(needle);
    });
  }, [filterModel, filterOptionQuery]);

  const selectAllVisibleOptions = useCallback(() => {
    if (!filterModel || filterModel.type !== "set") return;
    setColumnFilters((previous) => ({
      ...previous,
      [filterModel.key]: visibleFilterOptions.map((option) => option.value),
    }));
  }, [filterModel, visibleFilterOptions]);

  const filteredMatrixData = useMemo(() => {
    const filterKeys = ["roomCategory", "roomRate", "client", "dependentFrom"];

    return matrixData.filter((row) =>
      filterKeys.every((key) => {
        const selected = Array.isArray(columnFilters[key]) ? columnFilters[key] : [];
        if (selected.length === 0) return true;
        return selected.includes(normalizeFilterValue(getRowFilterLabel(row, key)));
      })
    );
  }, [columnFilters, getRowFilterLabel, matrixData]);

  const loadedRangeLabel =
    dates.length > 0
      ? matrixCopy.range.loaded(dates[0], dates[dates.length - 1], dates.length)
      : matrixCopy.range.noneLoaded;

  const subtitle = loading
    ? matrixCopy.subtitle.loading
    : (demoModeActive && error) ? matrixCopy.states.loadErrorTitle : matrixCopy.subtitle.ready(
        filteredMatrixData.length,
        matrixData.length,
        loadedRangeLabel,
        MAX_MATRIX_RANGE_DAYS
      );

  return (
    <div className={`rrm-page ${isLightTheme ? "rrm-page--light" : ""}`} data-demo-read-only={demoModeActive || undefined}>
      <style>{roomRatesMatrixCss}</style>

      <div className="rrm-card">
        <div className="rrm-cardHeader">
          <div>
            <h2 className="rrm-title">{matrixCopy.title}</h2>
            <div className="rrm-subtitle">{subtitle}</div>
          </div>

          <div className="rrm-toolbar">
            <div className="rrm-dateControls">
              <label className="rrm-label">
                {matrixCopy.toolbar.from}
                <input
                  type="date"
	                  className="rrm-input"
	                  value={range.start}
	                  min={requestStartMin}
	                  onChange={(event) => {
                    const nextStart = event.target.value;
                    const currentSpanDays =
                      range.start && range.end
                        ? Math.max(1, Math.min(MAX_MATRIX_RANGE_DAYS, dayDiff(range.start, range.end) + 1))
                        : DEFAULT_VISIBLE_DAYS;
                    const nextEnd =
                      range.end && range.end >= nextStart
                        ? range.end
                        : addDaysIso(nextStart, currentSpanDays - 1);

                    handleRequestedRangeChange(nextStart, nextEnd);
                  }}
                  disabled={loading}
                />
              </label>

              <label className="rrm-label">
                {matrixCopy.toolbar.to}
                <input
                  type="date"
                  className="rrm-input"
                  value={range.end}
                  min={requestEndMin}
                  max={requestEndMax}
                  onChange={(event) =>
                    handleRequestedRangeChange(range.start || event.target.value, event.target.value)
                  }
                  disabled={loading}
                />
              </label>

              <button
                type="button"
                className="rrm-btn rrm-btn--primary"
                onClick={() => {
                  if (dates.length === 0) return;
                  setRange(
                    sanitizeRange(
                      dates[0],
                      dates[Math.min(DEFAULT_VISIBLE_DAYS - 1, dates.length - 1)],
                      dates
                    )
                  );
                }}
                disabled={loading || dates.length === 0}
                title={matrixCopy.toolbar.resetRangeTitle}
              >
                {matrixCopy.toolbar.resetRange}
              </button>

              <span className={`rrm-queueBadge${isQueueProcessing || pendingTasksCount > 0 ? " isActive" : ""}`}>
                {demoModeActive ? demoCopy.readOnly : pendingTasksCount > 0
                  ? matrixCopy.queue.queuedChanges(pendingTasksCount)
                  : matrixCopy.queue.synced}
              </span>
            </div>

            <div className="rrm-toolbarActions">
              {demoModeActive ? (
                <button type="button" className="rrm-btn" disabled={loading} onClick={() => fetchMatrix()}>{demoCopy.refresh}</button>
              ) : null}
              {demoModeActive || typeof onOpenBulkApply === "function" ? (
                <button type="button" className="rrm-btn rrm-btn--green" disabled={demoModeActive} title={demoModeActive ? demoCopy.disabled : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) onOpenBulkApply?.(); }}>
                  {matrixCopy.toolbar.bulkApply}
                </button>
              ) : null}

              {demoModeActive || typeof onOpenStopSellsOverview === "function" ? (
                <button type="button" className="rrm-btn rrm-btn--amber" disabled={demoModeActive} title={demoModeActive ? demoCopy.disabled : undefined} onClick={() => { if (!demoModeActive && !isDemoMode()) onOpenStopSellsOverview?.(); }}>
                  {matrixCopy.toolbar.stopSellsOverview}
                </button>
              ) : null}
            </div>

            {queueError ? <div className="rrm-queueError">{queueError}</div> : null}
          </div>
        </div>

        <div className="rrm-tableWrap">
          {loading ? (
            <div className="rrm-state" role="status">
              <div className="rrm-stateTitle">{matrixCopy.states.loadingTitle}</div>
              <div className="rrm-stateSubtitle">{matrixCopy.states.loadingSubtitle}</div>
            </div>
          ) : error ? (
            <div className="rrm-state" role="alert">
              <div className="rrm-stateTitle">{matrixCopy.states.loadErrorTitle}</div>
              <div className="rrm-error">{error}</div>
              {demoModeActive && <button type="button" className="rrm-btn" onClick={() => fetchMatrix(lastDemoRequestRef.current || {})}>{demoCopy.retry}</button>}
            </div>
          ) : matrixData.length === 0 || visibleDates.length === 0 ? (
            <div className="rrm-state">
              <div className="rrm-stateTitle">{matrixCopy.states.noDataTitle}</div>
              <div className="rrm-stateSubtitle">{matrixCopy.states.noDataSubtitle}</div>
            </div>
          ) : filteredMatrixData.length === 0 ? (
            <div className="rrm-state">
              <div className="rrm-stateTitle">{matrixCopy.states.noFilteredTitle}</div>
              <div className="rrm-stateSubtitle">{matrixCopy.states.noFilteredSubtitle}</div>
            </div>
          ) : (
            <table className="rrm-table">
              <thead>
                <tr>
                  <th className="rrm-sticky s1 rrm-headCell">
                    <button
                      type="button"
                      data-rrm-filter-trigger="roomCategory"
                      className={`rrm-thBtn ${columnIsFiltered("roomCategory") ? "rrm-thBtn--filtered" : ""} ${
                        activeFilter?.key === "roomCategory" ? "rrm-thBtn--open" : ""
                      }`}
                      onClick={(event) => toggleFilterPopover("roomCategory", event.currentTarget)}
                    >
                      <span className="rrm-thLabel">{matrixCopy.headers.roomCategory}</span>
                      <span className="rrm-thSub">{columnSubtitle("roomCategory")}</span>
                      <span className={`rrm-thDot ${columnIsFiltered("roomCategory") ? "rrm-thDot--on" : ""}`} aria-hidden="true" />
                    </button>
                  </th>
                  <th className="rrm-sticky s2 rrm-headCell">
                    <button
                      type="button"
                      data-rrm-filter-trigger="roomRate"
                      className={`rrm-thBtn ${columnIsFiltered("roomRate") ? "rrm-thBtn--filtered" : ""} ${
                        activeFilter?.key === "roomRate" ? "rrm-thBtn--open" : ""
                      }`}
                      onClick={(event) => toggleFilterPopover("roomRate", event.currentTarget)}
                    >
                      <span className="rrm-thLabel">{matrixCopy.headers.roomRate}</span>
                      <span className="rrm-thSub">{columnSubtitle("roomRate")}</span>
                      <span className={`rrm-thDot ${columnIsFiltered("roomRate") ? "rrm-thDot--on" : ""}`} aria-hidden="true" />
                    </button>
                  </th>
                  <th className="rrm-sticky s3 rrm-headCell">
                    <button
                      type="button"
                      data-rrm-filter-trigger="client"
                      className={`rrm-thBtn ${columnIsFiltered("client") ? "rrm-thBtn--filtered" : ""} ${
                        activeFilter?.key === "client" ? "rrm-thBtn--open" : ""
                      }`}
                      onClick={(event) => toggleFilterPopover("client", event.currentTarget)}
                    >
                      <span className="rrm-thLabel">{matrixCopy.headers.client}</span>
                      <span className="rrm-thSub">{columnSubtitle("client")}</span>
                      <span className={`rrm-thDot ${columnIsFiltered("client") ? "rrm-thDot--on" : ""}`} aria-hidden="true" />
                    </button>
                  </th>
                  <th className="rrm-sticky s4 rrm-stickyShadow rrm-headCell">
                    <button
                      type="button"
                      data-rrm-filter-trigger="dependentFrom"
                      className={`rrm-thBtn ${columnIsFiltered("dependentFrom") ? "rrm-thBtn--filtered" : ""} ${
                        activeFilter?.key === "dependentFrom" ? "rrm-thBtn--open" : ""
                      }`}
                      onClick={(event) => toggleFilterPopover("dependentFrom", event.currentTarget)}
                    >
                      <span className="rrm-thLabel">{matrixCopy.headers.dependentFrom}</span>
                      <span className="rrm-thSub">{columnSubtitle("dependentFrom")}</span>
                      <span
                        className={`rrm-thDot ${columnIsFiltered("dependentFrom") ? "rrm-thDot--on" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>
                  {visibleDates.map((date) => (
                    <th key={date} className="rrm-dayHeader">
                      <div className="rrm-dayHeaderMain">{toDayLabel(date, dayLabelLocale)}</div>
                      <div className="rrm-dayHeaderSub">{date}</div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {filteredMatrixData.map((row) => {
                  const rowKey = rowKeyFor(row);

                  return (
                    <tr key={rowKey}>
                      <td className="rrm-sticky s1 rrm-metaCell">
                        <div className="rrm-metaText" title={String(row.room_category || "")}>
                          {row.room_category || "-"}
                        </div>
                      </td>

                      <td className="rrm-sticky s2 rrm-metaCell">
                        <button
                          type="button"
                          className="rrm-rateLink"
                          onClick={() => {
                            if (!row?.room_rate_id) return;
                            if (demoModeActive) {
                              navigate(`/edit-room-rate/${row.room_rate_id}?business_unit_id=${DEMO_BUSINESS_UNIT_ID}`);
                              return;
                            }
                            window.location.href = `/edit-room-rate/${row.room_rate_id}?business_unit_id=${businessUnitId}`;
                          }}
                          title={matrixCopy.actions.openRoomRate}
                        >
                          {row.room_rate || "-"}
                        </button>
                      </td>

                      <td className="rrm-sticky s3 rrm-metaCell">
                        <div className="rrm-metaText" title={String(row.client || "")}>
                          {row.client || "-"}
                        </div>
                      </td>

                      <td className="rrm-sticky s4 rrm-stickyShadow rrm-metaCell">
                        <div className="rrm-metaText" title={String(roomRateNameById.get(row.base_rate_id) || "-")}>
                          {row.base_rate_id ? roomRateNameById.get(row.base_rate_id) || "-" : "-"}
                        </div>
                      </td>

                      {visibleDates.map((date) => {
                        const cellKey = `${row.room_rate_id}|${date}`;

                        return (
                          <EditablePriceCell
                            key={`${rowKey}-${date}`}
                            row={row}
                            date={date}
                            value={row.prices?.[date]}
                            isStopSell={stopSellKeys.has(cellKey)}
                            isAutomatic={automaticStopSellKeys.has(cellKey)}
                            isPending={pendingCellKeys.has(cellKey)}
                            onToggleStopSell={toggleStopSell}
                            copy={matrixCopy}
                            formatPriceValue={formatPriceValue}
                            onCellPointerDown={handleCellPointerDown}
                            onCellPointerEnter={handleCellPointerEnter}
                            isDragSelected={dragKeys.has(cellKey)}
                            readOnly={demoModeActive}
                            readOnlyTitle={demoCopy.disabled}
                          />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {!demoModeActive && cellModal ? (
        <CellEditModal
          modal={cellModal}
          onCommit={commitCellModal}
          onCancel={cancelCellModal}
          copy={matrixCopy}
          isLightTheme={isLightTheme}
        />
      ) : null}

      {!demoModeActive && stopSellFailureNotice ? (
        <StopSellNoticeModal
          notice={stopSellFailureNotice}
          copy={matrixCopy}
          onClose={() => setStopSellFailureNotice(null)}
        />
      ) : null}

      {activeFilter?.key && filterModel && filterPopoverPos ? (
        <div
          ref={filterPopoverRef}
          className="rrm-filterPopover"
          style={{ top: filterPopoverPos.top, left: filterPopoverPos.left, width: filterPopoverPos.width }}
        >
            <div className="rrm-filterHeader">
              <div className="rrm-filterTitle">{filterModel.title}</div>
              <div className="rrm-filterHeaderActions">
                {columnIsFiltered(filterModel.key) ? (
                  <button type="button" className="rrm-filterSmallBtn" onClick={() => clearColumnFilter(filterModel.key)}>
                    {matrixCopy.filters.clear}
                  </button>
                ) : null}

                {filterModel.type === "set" ? (
                  <button type="button" className="rrm-filterSmallBtn" onClick={selectAllVisibleOptions}>
                    {matrixCopy.filters.all}
                  </button>
                ) : null}

                <button
                  type="button"
                  className="rrm-filterCloseBtn"
                  onClick={closeFilterPopover}
                  title={matrixCopy.filters.close}
                  aria-label={matrixCopy.filters.close}
                >
                  x
                </button>
              </div>
          </div>

          <div className="rrm-filterBody">
            <input
              className="rrm-filterInput"
              value={filterOptionQuery}
              onChange={(event) => setFilterOptionQuery(event.target.value)}
              placeholder={matrixCopy.filters.searchIn(
                String(filterModel.title || "").toLocaleLowerCase(filterSortLocale)
              )}
              autoFocus
            />

            <div className="rrm-filterList">
              {visibleFilterOptions.length === 0 ? (
                <div className="rrm-filterHint">{matrixCopy.filters.noMatchingOptions}</div>
              ) : (
                visibleFilterOptions.map((option) => {
                  const selected = Array.isArray(columnFilters[filterModel.key])
                    ? columnFilters[filterModel.key].includes(option.value)
                    : false;

                  return (
                    <label key={String(option.value)} className="rrm-filterOption" title={option.label}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleMultiSelectFilter(filterModel.key, option.value)}
                      />
                      <span className="rrm-filterOptionText">
                        <div className="rrm-filterOptionLabel">{option.label}</div>
                        {option.meta ? <div className="rrm-filterOptionMeta">{option.meta}</div> : null}
                      </span>
                      <span className="rrm-filterCountPill">{option.count}</span>
                    </label>
                  );
                })
              )}
            </div>

            <div className="rrm-filterHint">{matrixCopy.filters.selectHint}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
